<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import getWootAxios from 'dashboard/helper/getWootAxios';
import { buildAttributeOverrideDrilldownQuery } from '../../lib/attributeOverrideDrilldown';

const props = defineProps({
  assistant: {
    type: Object,
    default: null,
  },
  isSavingAll: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['openLegacy']);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();
const alert = useAlert();
const ESCALATION_SEED_QUERY_KEYS = [
  'escalation_seed',
  'escalation_seed_source',
  'escalation_seed_focus',
  'escalation_seed_reason',
  'escalation_seed_query',
  'escalation_seed_label',
];
const isSubmitting = ref(false);
const isLoadingRecentEscalations = ref(false);
const recentEscalations = ref([]);
const overrideEffectivenessByRuleIndex = ref({});
const overrideInboxBreakdownByRuleIndex = ref({});
const overrideConditionRollupsByRuleIndex = ref({});
const overrideGuardRollupsByRuleIndex = ref({});
const overrideConditionRecommendationsByRuleIndex = ref({});
const overrideGuardRecommendationsByRuleIndex = ref({});
const overrideRecentCasesByRuleIndex = ref({});
const overrideRecommendationsByRuleIndex = ref({});
const ATTRIBUTE_OVERRIDE_EFFECTIVENESS_DAYS = 7;
const ATTRIBUTE_OVERRIDE_EFFECTIVENESS_TIME_WINDOW = '7d';
const procedureRoutingSectionRef = ref(null);
const defaultBehaviorSectionRef = ref(null);
const escalationRulesSectionRef = ref(null);
const escalationGuidanceSectionRef = ref(null);

const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig']);
const aiCloserUIFlags = computed(() => store.getters['aiCloser/getUIFlags'] || {});
const isLoadingConfig = computed(() => aiCloserUIFlags.value.isFetchingConfig);
const isAIGamer = computed(
  () => props.assistant?.config?.wlab_sidebar_key === 'ai_gamer'
);

const automations = computed(() => store.getters['automations/getAutomations'] || []);
const inboxById = computed(() => store.getters['inboxes/getInbox']);
const templateModeEnabled = ref(false);
const procedureRoutingEnabled = ref(false);
const procedureRoutingPrecedence = ref('procedure_before_fallback');
const procedureStepConfirmationStrategy = ref('balanced');
const procedureFallbackAction = ref('handoff');
const uncertainAction = ref('fallback_ai');
const riskEnabled = ref(true);
const askClarifyTh = ref('');
const askClarifyEn = ref('');
const defaultReplyTh = ref('');
const defaultReplyEn = ref('');
const attributeOverrides = ref([]);

const openAutomationRules = () => {
  router.push(accountScopedRoute('automation_list'));
};

const openWorkspaceTab = tab => {
  const nextQuery = { ...(route.query || {}), tab };
  ESCALATION_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  router.replace({ query: nextQuery });
};

const openProceduresTab = () => openWorkspaceTab('train_procedures');
const openAnalyzeTab = () => openWorkspaceTab('analyze_decision_log');
const escalationSeedContext = computed(() => {
  const source = String(route.query?.escalation_seed_source || '').trim();
  const focus = String(route.query?.escalation_seed_focus || '').trim();
  const reason = String(route.query?.escalation_seed_reason || '').trim();
  const query = String(route.query?.escalation_seed_query || '').trim();
  const label = String(route.query?.escalation_seed_label || '').trim();
  const enabled =
    String(route.query?.escalation_seed || '').trim() === '1' ||
    [source, focus, reason, query, label].some(Boolean);

  return {
    enabled,
    source,
    focus,
    reason,
    query,
    label,
  };
});
const escalationSeedSummary = computed(() => {
  if (!escalationSeedContext.value.enabled) return '';

  const parts = [];
  if (escalationSeedContext.value.source) parts.push(`from ${escalationSeedContext.value.source}`);
  if (escalationSeedContext.value.focus === 'routing') parts.push('procedure and tool routing');
  if (escalationSeedContext.value.focus === 'defaults') parts.push('default behavior');
  if (escalationSeedContext.value.focus === 'rules') parts.push('escalation rules');
  if (escalationSeedContext.value.focus === 'guidance') parts.push('escalation guidance');
  if (escalationSeedContext.value.label) parts.push(escalationSeedContext.value.label);
  if (escalationSeedContext.value.reason) parts.push(escalationSeedContext.value.reason);
  return parts.join(' • ');
});
const escalationSeedPrimaryActionLabel = computed(() => {
  switch (escalationSeedContext.value.focus) {
    case 'routing':
      return 'Open routing';
    case 'defaults':
      return 'Open default behavior';
    case 'rules':
      return 'Open escalation rules';
    case 'guidance':
      return 'Open escalation guidance';
    default:
      return 'Open escalation area';
  }
});
async function focusSeededEscalationTarget() {
  if (!escalationSeedContext.value.enabled) return;
  await nextTick();

  switch (escalationSeedContext.value.focus) {
    case 'routing':
      procedureRoutingSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      break;
    case 'defaults':
      defaultBehaviorSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      break;
    case 'rules':
      escalationRulesSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      break;
    case 'guidance':
      escalationGuidanceSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      break;
    default:
      defaultBehaviorSectionRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      break;
  }
}
const clearEscalationSeed = async () => {
  const nextQuery = { ...(route.query || {}) };
  ESCALATION_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  await router.replace({ query: nextQuery });
};
const retestSeededEscalationContext = () => {
  if (!escalationSeedContext.value.query) return;
  const nextQuery = { ...(route.query || {}), tab: 'test_preview' };
  ESCALATION_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.content_seed;
  delete nextQuery.content_seed_source;
  delete nextQuery.content_seed_focus;
  delete nextQuery.content_seed_query;
  delete nextQuery.content_seed_reason;
  delete nextQuery.content_seed_scope_warning;
  delete nextQuery.guidance_seed;
  delete nextQuery.guidance_seed_source;
  delete nextQuery.guidance_seed_editor;
  delete nextQuery.guidance_seed_reason;
  delete nextQuery.guidance_seed_query;
  delete nextQuery.deploy_seed;
  delete nextQuery.deploy_seed_source;
  delete nextQuery.deploy_seed_focus;
  delete nextQuery.deploy_seed_reason;
  delete nextQuery.deploy_seed_inbox_id;
  delete nextQuery.analyze_seed;
  delete nextQuery.analyze_source;
  delete nextQuery.analyze_time_window;
  delete nextQuery.analyze_inbox_id;
  delete nextQuery.analyze_content_gap_reason;
  delete nextQuery.analyze_article_scope_warning;
  delete nextQuery.analyze_delivery_mode;
  nextQuery.test_query = escalationSeedContext.value.query;
  nextQuery.test_reason = escalationSeedContext.value.reason || 'escalation_seed';
  nextQuery.test_source = 'Escalation';
  router.replace({ query: nextQuery });
};

const highRiskAction = ref('auto_suggest');
const attributeOverrideMatchers = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'in', label: 'In list' },
  { value: 'regex', label: 'Regex' },
  { value: 'present', label: 'Is present' },
  { value: 'boolean', label: 'Boolean' },
];
const attributeOverrideConditionGroups = [
  { key: 'all', label: 'All of these must match' },
  { key: 'any', label: 'At least one of these must match' },
  { key: 'not', label: 'None of these may match' },
];
const overrideEffectivenessCreatedAfterIso = computed(() => {
  const next = new Date();
  next.setDate(next.getDate() - ATTRIBUTE_OVERRIDE_EFFECTIVENESS_DAYS);
  return next.toISOString();
});

const resolveInboxName = id => {
  const num = Number(id);
  if (!Number.isFinite(num)) return '';
  const inbox = inboxById.value?.(num) || {};
  return inbox?.name || String(num);
};
const timestampMs = value => {
  if (!value) return 0;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};
const formatDateTime = value => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};
const formatDurationSeconds = value => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '';
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

const inferOverrideMatcher = override => {
  const matcher = String(override?.matcher || '').trim().toLowerCase();
  if (matcher) return matcher;
  if (Array.isArray(override?.values) && override.values.length) return 'in';
  if (override?.boolean_value === true || override?.boolean_value === false) {
    return 'boolean';
  }
  return 'equals';
};

const matcherSummary = ({ matcher, configuredValue }) => {
  switch (matcher) {
    case 'contains':
      return `contains "${configuredValue}"`;
    case 'starts_with':
      return `starts with "${configuredValue}"`;
    case 'ends_with':
      return `ends with "${configuredValue}"`;
    case 'in':
      return `in [${configuredValue}]`;
    case 'regex':
      return `regex ${configuredValue}`;
    case 'present':
      return 'is present';
    case 'boolean':
      return `is ${configuredValue}`;
    default:
      return `equals "${configuredValue}"`;
  }
};

const draftMatcherSummary = override => {
  const matcher = inferOverrideMatcher(override);
  const configuredValue =
    matcher === 'in'
      ? String(override?.values_text || '')
      : matcher === 'boolean'
        ? String(override?.boolean_value || 'true')
        : String(override?.value || override?.equals || '');
  return matcherSummary({ matcher, configuredValue }).trim();
};

const conditionDraftSummary = condition => {
  const attributeKey = String(condition?.attribute_key || '').trim();
  if (!attributeKey) return 'Incomplete condition';
  return `${attributeKey} ${draftMatcherSummary(condition)}`.trim();
};

const overrideDraftSummary = override => {
  const groups = override?.condition_groups || {};
  return attributeOverrideConditionGroups
    .filter(group => Array.isArray(groups[group.key]) && groups[group.key].length)
    .map(group => {
      const items = groups[group.key]
        .map(condition => conditionDraftSummary(condition))
        .join('; ');
      return `${group.key}: ${items}`;
    })
    .join(' | ');
};

const overrideMatcherValueLabel = matcher => {
  switch (matcher) {
    case 'regex':
      return 'Regex pattern';
    case 'in':
      return 'Allowed values';
    case 'boolean':
      return 'Expected boolean';
    default:
      return 'Match value';
  }
};

const overrideMatcherPlaceholder = matcher => {
  switch (matcher) {
    case 'contains':
      return 'vip';
    case 'starts_with':
      return 'loan_';
    case 'ends_with':
      return '_blocked';
    case 'in':
      return 'vip, premium, gold';
    case 'regex':
      return 'late|overdue';
    default:
      return 'deposit';
  }
};

const overrideEffectivenessForRule = index =>
  overrideEffectivenessByRuleIndex.value[index] || {
    usedCount: 0,
    resolvedCount: 0,
    escalatedCount: 0,
    unresolvedCount: 0,
    resolvedRate: 0,
    escalatedRate: 0,
    topInboxId: null,
    topInboxUsedCount: 0,
    topInboxResolvedCount: 0,
    topInboxEscalatedCount: 0,
  };

const overrideRecentHitCount = index =>
  Number(overrideEffectivenessForRule(index).usedCount || 0);
const overrideResolvedCount = index =>
  Number(overrideEffectivenessForRule(index).resolvedCount || 0);
const overrideEscalatedCount = index =>
  Number(overrideEffectivenessForRule(index).escalatedCount || 0);
const overrideUnresolvedCount = index =>
  Number(overrideEffectivenessForRule(index).unresolvedCount || 0);
const overrideResolvedRate = index =>
  Number(overrideEffectivenessForRule(index).resolvedRate || 0);
const overrideEscalatedRate = index =>
  Number(overrideEffectivenessForRule(index).escalatedRate || 0);
const overrideTopInboxId = index => Number(overrideEffectivenessForRule(index).topInboxId);
const overrideTopInboxLabel = index => {
  const inboxId = overrideTopInboxId(index);
  if (!Number.isFinite(inboxId) || inboxId <= 0) return '';
  return resolveInboxName(inboxId) || `Inbox ${inboxId}`;
};
const overrideTopInboxUsedCount = index =>
  Number(overrideEffectivenessForRule(index).topInboxUsedCount || 0);
const overrideTopInboxResolvedCount = index =>
  Number(overrideEffectivenessForRule(index).topInboxResolvedCount || 0);
const overrideTopInboxEscalatedCount = index =>
  Number(overrideEffectivenessForRule(index).topInboxEscalatedCount || 0);
const overrideInboxBreakdownRows = index =>
  [...(overrideInboxBreakdownByRuleIndex.value[index] || [])].sort(
    (a, b) =>
      (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
      (b.unresolvedCount || 0) - (a.unresolvedCount || 0) ||
      (b.usedCount || 0) - (a.usedCount || 0) ||
      (a.inboxId || 0) - (b.inboxId || 0)
  );
const formatOverrideInboxBreakdownRow = row => {
  const inboxId = Number(row?.inboxId);
  const inboxLabel = resolveInboxName(inboxId) || `Inbox ${inboxId}`;
  return `${inboxLabel} ${row?.escalatedCount || 0}/${row?.usedCount || 0} escalated`;
};
const overrideRecentCaseRows = index =>
  [...(overrideRecentCasesByRuleIndex.value[index] || [])].sort(
    (a, b) =>
      timestampMs(b.occurredAt) - timestampMs(a.occurredAt) ||
      (b.decisionEventId || 0) - (a.decisionEventId || 0)
  );
const formatOverrideRecentCaseRow = row => {
  const inboxId = Number(row?.inboxId);
  const inboxLabel = resolveInboxName(inboxId) || `Inbox ${inboxId}`;
  const outcome = String(row?.outcome || 'pending').trim() || 'pending';
  if (!row?.outcomeAt) return `${inboxLabel} ${outcome} since ${formatDateTime(row?.occurredAt)}`;

  const durationLabel = formatDurationSeconds(row?.timeToOutcomeSeconds);
  return `${inboxLabel} ${outcome} ${formatDateTime(row?.occurredAt)} -> ${formatDateTime(row?.outcomeAt)}${durationLabel ? ` (${durationLabel})` : ''}`;
};
const slotSchema = computed(
  () => aiCloserConfig.value?.prompt_sections?.slot_schema || { enabled: false, intents: {} }
);
const procedureIntentEntries = computed(() => {
  const intents = slotSchema.value?.intents || {};
  return Object.entries(intents).map(([key, intent]) => ({
    key,
    title: String(intent?.title || intent?.label || key).trim() || key,
    completionActions: Array.isArray(intent?.completion_actions)
      ? intent.completion_actions.length
      : 0,
  }));
});
const procedureIntentCount = computed(() => procedureIntentEntries.value.length);
const procedureCompletionActionCount = computed(() =>
  procedureIntentEntries.value.reduce(
    (sum, intent) => sum + Number(intent.completionActions || 0),
    0
  )
);
const procedureRoutingPrecedenceLabel = computed(() => {
  switch (String(procedureRoutingPrecedence.value || '').trim()) {
    case 'fallback_before_procedure':
      return 'Fallback before procedure';
    default:
      return 'Procedure before fallback';
  }
});
const procedureStepConfirmationLabel = computed(() => {
  switch (String(procedureStepConfirmationStrategy.value || '').trim()) {
    case 'confirm_each_step':
      return 'Confirm each step';
    case 'collect_brief_batch':
      return 'Collect a short batch first';
    default:
      return 'Balanced';
  }
});
const procedureFallbackActionLabel = computed(() => {
  switch (String(procedureFallbackAction.value || '').trim()) {
    case 'ask_clarify':
      return 'Ask one more clarifying question';
    case 'default_reply':
      return 'Send a neutral holding reply';
    default:
      return 'Handoff to a human';
  }
});
const uncertainActionLabel = computed(() => {
  switch (String(uncertainAction.value || '').trim()) {
    case 'ask_clarify':
      return 'Ask a clarifying question';
    case 'default_reply':
      return 'Send the default fallback reply';
    default:
      return 'Continue with full AI';
  }
});
const highRiskActionLabel = computed(() => {
  switch (String(highRiskAction.value || '').trim()) {
    case 'handoff':
      return 'Force human handoff';
    case 'suggest':
      return 'Suggest only';
    case 'inherit':
      return 'Inherit current mode';
    default:
      return 'Auto suggest';
  }
});
const procedureRoutingCards = computed(() => [
  {
    label: 'Procedure intents',
    value: procedureIntentCount.value,
    note:
      procedureIntentCount.value > 0
        ? `${procedureCompletionActionCount.value} completion action${procedureCompletionActionCount.value === 1 ? '' : 's'} currently configured in Procedures.`
        : 'No procedure intents are configured yet.',
  },
  {
    label: 'Routing active',
    value: procedureRoutingEnabled.value ? 'On' : 'Off',
    note: procedureRoutingEnabled.value
      ? `${procedureRoutingPrecedenceLabel.value} is currently live when template routing misses.`
      : 'Procedure routing is currently disabled at the wrapper level.',
  },
  {
    label: 'Step confirmation',
    value: procedureStepConfirmationLabel.value,
    note:
      'This default only applies when an individual procedure does not set its own question cadence.',
  },
  {
    label: 'If no route matches',
    value: uncertainActionLabel.value,
    note:
      templateModeEnabled.value
        ? 'This is the real runtime fallback used by template-mode routing.'
        : 'Template-mode fallback is currently disabled.',
  },
  {
    label: 'High-risk outcome',
    value: highRiskActionLabel.value,
    note: riskEnabled.value
      ? 'This is the current default L3 action used by risk policy.'
      : 'Risk policy is currently disabled.',
  },
  {
    label: 'Procedure fallback',
    value: procedureFallbackActionLabel.value,
    note:
      'This default only applies when a procedure flow does not define its own fallback action.',
  },
]);
const latestOverrideRecentCase = index => overrideRecentCaseRows(index)[0] || null;
const latestOverrideRecentCaseByOutcome = (index, outcome) =>
  overrideRecentCaseRows(index).find(row => String(row?.outcome || '').trim() === outcome) || null;
const recentOverrideMatchedConditionSummaries = index =>
  [...new Set(overrideRecentCaseRows(index).flatMap(row => row?.matchedConditionSummaries || []))]
    .filter(Boolean)
    .slice(0, 3);
const recentOverridePassedGuardSummaries = index =>
  [...new Set(overrideRecentCaseRows(index).flatMap(row => row?.passedGuardSummaries || []))]
    .filter(Boolean)
    .slice(0, 3);
const overrideConditionRollupRows = index =>
  [...(overrideConditionRollupsByRuleIndex.value[index] || [])].sort(
    (a, b) =>
      (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
      (b.unresolvedCount || 0) - (a.unresolvedCount || 0) ||
      (b.usedCount || 0) - (a.usedCount || 0) ||
      (a.label || '').localeCompare(b.label || '')
  );
const overrideGuardRollupRows = index =>
  [...(overrideGuardRollupsByRuleIndex.value[index] || [])].sort(
    (a, b) =>
      (b.escalatedCount || 0) - (a.escalatedCount || 0) ||
      (b.unresolvedCount || 0) - (a.unresolvedCount || 0) ||
      (b.usedCount || 0) - (a.usedCount || 0) ||
      (a.label || '').localeCompare(b.label || '')
  );
const topOverrideConditionRollupRows = index =>
  overrideConditionRollupRows(index).slice(0, 3);
const topOverrideGuardRollupRows = index =>
  overrideGuardRollupRows(index).slice(0, 3);
const overrideConditionRecommendationRows = index =>
  [...(overrideConditionRecommendationsByRuleIndex.value[index] || [])];
const overrideGuardRecommendationRows = index =>
  [...(overrideGuardRecommendationsByRuleIndex.value[index] || [])];
const topOverrideConditionRecommendation = index =>
  overrideConditionRecommendationRows(index)[0] || null;
const topOverrideGuardRecommendation = index =>
  overrideGuardRecommendationRows(index)[0] || null;
const formatOverrideBranchRollupRow = row =>
  `${row?.label || ''} ${row?.escalatedCount || 0}/${row?.usedCount || 0} escalated • ${row?.unresolvedCount || 0} pending`;
const formatOverrideBranchTopInbox = row => {
  const inboxId = Number(row?.topInboxId);
  const inboxLabel = resolveInboxName(inboxId) || `Inbox ${inboxId}`;
  if (!Number.isFinite(inboxId) || inboxId <= 0) return '';
  return `${inboxLabel} ${row?.topInboxEscalatedCount || 0}/${row?.topInboxUsedCount || 0} escalated`;
};
const formatOverrideBranchLatestCase = row => {
  const inboxId = Number(row?.latestInboxId);
  const inboxLabel = resolveInboxName(inboxId) || `Inbox ${inboxId}`;
  const outcome = String(row?.latestOutcome || 'pending').trim() || 'pending';
  if (!row?.latestOccurredAt) return '';
  if (!row?.latestOutcomeAt) {
    return `${inboxLabel} ${outcome} since ${formatDateTime(row?.latestOccurredAt)}`;
  }

  const durationLabel = formatDurationSeconds(row?.latestTimeToOutcomeSeconds);
  return `${inboxLabel} ${outcome} ${formatDateTime(row?.latestOccurredAt)} -> ${formatDateTime(row?.latestOutcomeAt)}${durationLabel ? ` (${durationLabel})` : ''}`;
};
const formatOverrideBranchComparedCase = (row, outcome) => {
  const normalizedOutcome = String(outcome || '').trim();
  const isResolved = normalizedOutcome === 'resolved';
  const inboxId = Number(
    isResolved ? row?.latestResolvedInboxId : row?.latestEscalatedInboxId
  );
  const inboxLabel = resolveInboxName(inboxId) || `Inbox ${inboxId}`;
  const occurredAt = isResolved
    ? row?.latestResolvedOccurredAt
    : row?.latestEscalatedOccurredAt;
  const outcomeAt = isResolved
    ? row?.latestResolvedOutcomeAt
    : row?.latestEscalatedOutcomeAt;
  const timeToOutcomeSeconds = isResolved
    ? row?.latestResolvedTimeToOutcomeSeconds
    : row?.latestEscalatedTimeToOutcomeSeconds;
  if (!occurredAt) return '';
  if (!outcomeAt) {
    return `${inboxLabel} ${normalizedOutcome} since ${formatDateTime(occurredAt)}`;
  }

  const durationLabel = formatDurationSeconds(timeToOutcomeSeconds);
  return `${inboxLabel} ${normalizedOutcome} ${formatDateTime(occurredAt)} -> ${formatDateTime(outcomeAt)}${durationLabel ? ` (${durationLabel})` : ''}`;
};
const overrideBranchRecommendationOutcome = row => {
  const recommendationCode = String(row?.recommendationCode || '').trim();
  if (
    recommendationCode === 'split_by_inbox' ||
    recommendationCode === 'reduce_escalation_pressure'
  ) {
    return 'escalated';
  }
  if (
    recommendationCode === 'investigate_pending_cases' ||
    recommendationCode === 'tighten_for_resolution'
  ) {
    return 'pending';
  }
  return '';
};
const formatOverrideRecentSummaryList = rows => rows.join(' • ');
const formatLatestOverrideRecentCaseByOutcome = (index, outcome) => {
  const row = latestOverrideRecentCaseByOutcome(index, outcome);
  if (!row) return '';
  return formatOverrideRecentCaseRow(row);
};
const openOverrideAnalyzeDrilldown = async options => {
  await router.push({
    query: {
      ...buildAttributeOverrideDrilldownQuery(route.query, {
        timeWindow: ATTRIBUTE_OVERRIDE_EFFECTIVENESS_TIME_WINDOW,
        ...options,
      }),
      tab: 'analyze_decision_log',
    },
  });
};
const inspectOverrideRuleInAnalyze = (index, override) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
  });
const inspectOverrideOutcomeInAnalyze = (index, override, outcome) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    outcome,
  });
const inspectOverrideConditionInAnalyze = (index, override, condition) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    condition,
  });
const inspectOverrideConditionTopInboxInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    condition: row?.label,
    inboxId: row?.topInboxId,
  });
const inspectOverrideConditionLatestCaseInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    condition: row?.label,
    inboxId: row?.latestInboxId,
    conversationId: row?.latestConversationId,
    outcome: row?.latestOutcome,
  });
const inspectOverrideConditionResolvedCaseInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    condition: row?.label,
    inboxId: row?.latestResolvedInboxId,
    conversationId: row?.latestResolvedConversationId,
    outcome: 'resolved',
  });
const inspectOverrideConditionEscalatedCaseInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    condition: row?.label,
    inboxId: row?.latestEscalatedInboxId,
    conversationId: row?.latestEscalatedConversationId,
    outcome: 'escalated',
  });
const inspectOverrideConditionRecommendationInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    condition: row?.label,
    outcome:
      row?.fixPresetDrilldownOutcome || overrideBranchRecommendationOutcome(row),
    inboxId: row?.fixPresetDrilldownInboxId,
    fixIntentCode: row?.fixIntentCode,
    fixIntentTitle: row?.fixIntentTitle,
  });
const inspectOverrideConditionRepairBundleActionInAnalyze = (
  index,
  override,
  row,
  actionKey
) => {
  const baseOptions = {
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    condition: row?.label,
    fixIntentCode: row?.fixIntentCode,
    fixIntentTitle: row?.fixIntentTitle,
  };
  switch (String(actionKey || '').trim()) {
    case 'hotspot':
      return openOverrideAnalyzeDrilldown({
        ...baseOptions,
        compareStageCode: row?.repairBundleHotspotStageCode,
        compareStageTitle: row?.repairBundleHotspotStageTitle,
        inboxId: row?.repairBundleHotspotActionInboxId,
      });
    case 'resolved':
      return openOverrideAnalyzeDrilldown({
        ...baseOptions,
        compareStageCode: row?.repairBundleResolvedStageCode,
        compareStageTitle: row?.repairBundleResolvedStageTitle,
        inboxId: row?.repairBundleResolvedActionInboxId,
        conversationId: row?.repairBundleResolvedActionConversationId,
        outcome: row?.repairBundleResolvedActionOutcome,
      });
    case 'escalated':
      return openOverrideAnalyzeDrilldown({
        ...baseOptions,
        compareStageCode: row?.repairBundleEscalatedStageCode,
        compareStageTitle: row?.repairBundleEscalatedStageTitle,
        inboxId: row?.repairBundleEscalatedActionInboxId,
        conversationId: row?.repairBundleEscalatedActionConversationId,
        outcome: row?.repairBundleEscalatedActionOutcome,
      });
    case 'validation':
      return openOverrideAnalyzeDrilldown({
        ...baseOptions,
        compareStageCode: row?.repairBundleValidationStageCode,
        compareStageTitle: row?.repairBundleValidationStageTitle,
        inboxId: row?.repairBundleValidationActionInboxId,
        outcome: row?.repairBundleValidationActionOutcome,
      });
    default:
      return null;
  }
};
const inspectOverrideConditionCompareInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    condition: row?.label,
  });
const inspectOverrideGuardInAnalyze = (index, override, guard) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    guard,
  });
const inspectOverrideGuardTopInboxInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    guard: row?.label,
    inboxId: row?.topInboxId,
  });
const inspectOverrideGuardLatestCaseInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    guard: row?.label,
    inboxId: row?.latestInboxId,
    conversationId: row?.latestConversationId,
    outcome: row?.latestOutcome,
  });
const inspectOverrideGuardResolvedCaseInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    guard: row?.label,
    inboxId: row?.latestResolvedInboxId,
    conversationId: row?.latestResolvedConversationId,
    outcome: 'resolved',
  });
const inspectOverrideGuardEscalatedCaseInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    guard: row?.label,
    inboxId: row?.latestEscalatedInboxId,
    conversationId: row?.latestEscalatedConversationId,
    outcome: 'escalated',
  });
const inspectOverrideGuardRecommendationInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    guard: row?.label,
    outcome:
      row?.fixPresetDrilldownOutcome || overrideBranchRecommendationOutcome(row),
    inboxId: row?.fixPresetDrilldownInboxId,
    fixIntentCode: row?.fixIntentCode,
    fixIntentTitle: row?.fixIntentTitle,
  });
const inspectOverrideGuardRepairBundleActionInAnalyze = (
  index,
  override,
  row,
  actionKey
) => {
  const baseOptions = {
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    guard: row?.label,
    fixIntentCode: row?.fixIntentCode,
    fixIntentTitle: row?.fixIntentTitle,
  };
  switch (String(actionKey || '').trim()) {
    case 'hotspot':
      return openOverrideAnalyzeDrilldown({
        ...baseOptions,
        compareStageCode: row?.repairBundleHotspotStageCode,
        compareStageTitle: row?.repairBundleHotspotStageTitle,
        inboxId: row?.repairBundleHotspotActionInboxId,
      });
    case 'resolved':
      return openOverrideAnalyzeDrilldown({
        ...baseOptions,
        compareStageCode: row?.repairBundleResolvedStageCode,
        compareStageTitle: row?.repairBundleResolvedStageTitle,
        inboxId: row?.repairBundleResolvedActionInboxId,
        conversationId: row?.repairBundleResolvedActionConversationId,
        outcome: row?.repairBundleResolvedActionOutcome,
      });
    case 'escalated':
      return openOverrideAnalyzeDrilldown({
        ...baseOptions,
        compareStageCode: row?.repairBundleEscalatedStageCode,
        compareStageTitle: row?.repairBundleEscalatedStageTitle,
        inboxId: row?.repairBundleEscalatedActionInboxId,
        conversationId: row?.repairBundleEscalatedActionConversationId,
        outcome: row?.repairBundleEscalatedActionOutcome,
      });
    case 'validation':
      return openOverrideAnalyzeDrilldown({
        ...baseOptions,
        compareStageCode: row?.repairBundleValidationStageCode,
        compareStageTitle: row?.repairBundleValidationStageTitle,
        inboxId: row?.repairBundleValidationActionInboxId,
        outcome: row?.repairBundleValidationActionOutcome,
      });
    default:
      return null;
  }
};
const inspectOverrideGuardCompareInAnalyze = (index, override, row) =>
  openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    guard: row?.label,
  });
const inspectLatestOverrideCaseInAnalyze = (index, override) => {
  const latestCase = latestOverrideRecentCase(index);
  return openOverrideAnalyzeDrilldown({
    ruleIndex: index,
    label: String(override?.label || '').trim() || `Rule ${index}`,
    action: String(override?.action || '').trim(),
    inboxId: latestCase?.inboxId,
    conversationId: latestCase?.conversationId,
    outcome: latestCase?.outcome,
  });
};
const overrideRecommendationForRule = index =>
  overrideRecommendationsByRuleIndex.value[index] || {
    title: '',
    body: '',
    evidenceSummary: '',
    suggestedChange: '',
    nextStep: '',
    severity: '',
    recommendationCode: '',
  };
const overrideRecommendationTitle = index =>
  String(overrideRecommendationForRule(index).title || '').trim();
const overrideRecommendationBody = index =>
  String(overrideRecommendationForRule(index).body || '').trim();
const overrideRecommendationEvidence = index =>
  String(overrideRecommendationForRule(index).evidenceSummary || '').trim();
const overrideRecommendationSuggestedChange = index =>
  String(overrideRecommendationForRule(index).suggestedChange || '').trim();
const overrideRecommendationNextStep = index =>
  String(overrideRecommendationForRule(index).nextStep || '').trim();
const overrideRecommendationSeverity = index =>
  String(overrideRecommendationForRule(index).severity || '').trim();
const overrideRecommendationSeverityClass = index => {
  switch (overrideRecommendationSeverity(index)) {
    case 'danger':
      return 'text-red-700';
    case 'warning':
      return 'text-amber-700';
    default:
      return 'fin-muted';
  }
};

const buildAttributeOverrideConditionDraft = () => ({
  id: `condition-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  attribute_key: '',
  matcher: 'equals',
  value: '',
  values_text: '',
  boolean_value: 'true',
});

const normalizeAttributeOverrideConditionDraft = condition => ({
  id:
    condition?.id ||
    `condition-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  attribute_key: (condition?.attribute_key || '').toString(),
  matcher: inferOverrideMatcher(condition),
  value: (condition?.value || condition?.equals || '').toString(),
  values_text: Array.isArray(condition?.values)
    ? condition.values.map(item => item?.toString?.() || '').filter(Boolean).join(', ')
    : '',
  boolean_value:
    condition?.boolean_value === false ||
    String(condition?.boolean_value).trim() === 'false'
      ? 'false'
      : 'true',
});

const normalizeAttributeOverrideConditionGroups = override => {
  const groups = override?.conditions || {};
  if (groups && typeof groups === 'object' && !Array.isArray(groups)) {
    return {
      all: Array.isArray(groups.all)
        ? groups.all.map(normalizeAttributeOverrideConditionDraft)
        : [],
      any: Array.isArray(groups.any)
        ? groups.any.map(normalizeAttributeOverrideConditionDraft)
        : [],
      not: Array.isArray(groups.not)
        ? groups.not.map(normalizeAttributeOverrideConditionDraft)
        : [],
    };
  }

  const legacyCondition = normalizeAttributeOverrideConditionDraft(override);
  const hasLegacyCondition =
    legacyCondition.attribute_key ||
    legacyCondition.value ||
    legacyCondition.values_text;

  return {
    all: hasLegacyCondition ? [legacyCondition] : [buildAttributeOverrideConditionDraft()],
    any: [],
    not: [],
  };
};

const recentEscalationRows = computed(() =>
  recentEscalations.value.map(event => {
    const payload = event?.payload || {};
    const triggerLabel =
      payload.attribute_override_label ||
      [payload.attribute_override_key, payload.attribute_override_value]
        .filter(Boolean)
        .join(' = ') ||
      'Attribute override';
    const outcome =
      payload.attribute_override_action ||
      payload.intent_risk_action ||
      payload.action ||
      payload.delivery_mode ||
      '-';
    return {
      id: event.id,
      trigger: triggerLabel,
      detail:
        payload.attribute_override_condition_summary
          ? payload.attribute_override_condition_summary
          : payload.attribute_override_key &&
              payload.attribute_override_match_type &&
              payload.attribute_override_configured_value
            ? `${payload.attribute_override_key} ${matcherSummary({
                matcher: payload.attribute_override_match_type,
                configuredValue: payload.attribute_override_configured_value,
              })}`
            : payload.attribute_override_key && payload.attribute_override_value
              ? `${payload.attribute_override_key} = ${payload.attribute_override_value}`
              : '',
      outcome,
      channel: resolveInboxName(event.inbox_id) || 'Unknown',
      time: event.created_at
        ? new Date(event.created_at).toLocaleString()
        : '-',
    };
  })
);

const buildAttributeOverrideDraft = () => ({
  id: `override-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  enabled: true,
  label: '',
  condition_groups: {
    all: [buildAttributeOverrideConditionDraft()],
    any: [],
    not: [],
  },
  risk_tier: 'L3',
  action: 'handoff',
});

const normalizeAttributeOverrideDrafts = overrides =>
  Array.isArray(overrides)
    ? overrides.map((override, index) => ({
        id: override?.id || `override-${index}-${Math.random().toString(36).slice(2, 8)}`,
        enabled: override?.enabled !== false,
        label: (override?.label || '').toString(),
        condition_groups: normalizeAttributeOverrideConditionGroups(override),
        risk_tier: ['L0', 'L1', 'L2', 'L3'].includes(
          String(override?.risk_tier || '').trim().toUpperCase()
        )
          ? String(override.risk_tier).trim().toUpperCase()
          : 'L3',
        action: ['inherit', 'auto_suggest', 'suggest', 'handoff'].includes(
          String(override?.action || '').trim()
        )
          ? String(override.action).trim()
          : 'handoff',
      }))
    : [];

const buildAttributeOverrideConditionPayload = (condition, label) => {
  const matcher = inferOverrideMatcher(condition);
  const attributeKey = String(condition?.attribute_key || '').trim();
  const value = String(condition?.value || '').trim();
  const values = String(condition?.values_text || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
  const booleanValue = String(condition?.boolean_value || 'true').trim() === 'false' ? false : true;

  if (!attributeKey) {
    throw new Error(`${label} needs an attribute key`);
  }
  if (matcher === 'in' && !values.length) {
    throw new Error(`${label} needs at least one list value`);
  }
  if (
    ['equals', 'contains', 'starts_with', 'ends_with', 'regex'].includes(matcher) &&
    !value
  ) {
    throw new Error(`${label} needs a match value`);
  }

  if (matcher === 'equals') {
    return {
      attribute_key: attributeKey,
      matcher,
      equals: value,
    };
  }
  if (matcher === 'present') {
    return {
      attribute_key: attributeKey,
      matcher,
    };
  }
  if (matcher === 'boolean') {
    return {
      attribute_key: attributeKey,
      matcher,
      boolean_value: booleanValue,
    };
  }
  if (matcher === 'in') {
    return {
      attribute_key: attributeKey,
      matcher,
      values,
    };
  }
  return {
    attribute_key: attributeKey,
    matcher,
    value,
  };
};

const buildAttributeOverridePayload = () =>
  attributeOverrides.value
    .map(override => {
      const groups = override.condition_groups || { all: [], any: [], not: [] };
      const payloadGroups = {
        all: Array.isArray(groups.all)
          ? groups.all
              .filter(condition => {
                const matcher = inferOverrideMatcher(condition);
                return !!(
                  String(condition?.attribute_key || '').trim() ||
                  String(condition?.value || '').trim() ||
                  String(condition?.values_text || '').trim() ||
                  matcher === 'present' ||
                  matcher === 'boolean'
                );
              })
              .map((condition, conditionIndex) =>
                buildAttributeOverrideConditionPayload(
                  condition,
                  `Attribute escalation rule "${override.label || 'unnamed'}" all-condition ${conditionIndex + 1}`
                )
              )
          : [],
        any: Array.isArray(groups.any)
          ? groups.any
              .filter(condition => {
                const matcher = inferOverrideMatcher(condition);
                return !!(
                  String(condition?.attribute_key || '').trim() ||
                  String(condition?.value || '').trim() ||
                  String(condition?.values_text || '').trim() ||
                  matcher === 'present' ||
                  matcher === 'boolean'
                );
              })
              .map((condition, conditionIndex) =>
                buildAttributeOverrideConditionPayload(
                  condition,
                  `Attribute escalation rule "${override.label || 'unnamed'}" any-condition ${conditionIndex + 1}`
                )
              )
          : [],
        not: Array.isArray(groups.not)
          ? groups.not
              .filter(condition => {
                const matcher = inferOverrideMatcher(condition);
                return !!(
                  String(condition?.attribute_key || '').trim() ||
                  String(condition?.value || '').trim() ||
                  String(condition?.values_text || '').trim() ||
                  matcher === 'present' ||
                  matcher === 'boolean'
                );
              })
              .map((condition, conditionIndex) =>
                buildAttributeOverrideConditionPayload(
                  condition,
                  `Attribute escalation rule "${override.label || 'unnamed'}" not-condition ${conditionIndex + 1}`
                )
              )
          : [],
      };

      return {
        enabled: !!override.enabled,
        label: override.label.trim(),
        risk_tier: override.risk_tier,
        action: override.action,
        payloadGroups,
      };
    })
    .filter(override => {
      const isCompletelyBlank =
        !override.label &&
        !override.payloadGroups.all.length &&
        !override.payloadGroups.any.length &&
        !override.payloadGroups.not.length;
      return !isCompletelyBlank;
    })
    .map((override, index) => {
      if (!override.payloadGroups.all.length && !override.payloadGroups.any.length) {
        throw new Error(
          `Attribute escalation rule ${index + 1} needs at least one all/any condition`
        );
      }

      const hasOnlyOneSimpleCondition =
        override.payloadGroups.all.length === 1 &&
        !override.payloadGroups.any.length &&
        !override.payloadGroups.not.length;

      if (hasOnlyOneSimpleCondition) {
        const primaryCondition = override.payloadGroups.all[0];
        return {
          enabled: override.enabled,
          label: override.label,
          attribute_key: primaryCondition.attribute_key,
          matcher: primaryCondition.matcher,
          equals: primaryCondition.equals,
          value: primaryCondition.value,
          values: primaryCondition.values,
          boolean_value: primaryCondition.boolean_value,
          risk_tier: override.risk_tier,
          action: override.action,
        };
      }

      return {
        enabled: override.enabled,
        label: override.label,
        conditions: {
          ...(override.payloadGroups.all.length
            ? { all: override.payloadGroups.all }
            : {}),
          ...(override.payloadGroups.any.length
            ? { any: override.payloadGroups.any }
            : {}),
          ...(override.payloadGroups.not.length
            ? { not: override.payloadGroups.not }
            : {}),
        },
        risk_tier: override.risk_tier,
        action: override.action,
      };
    });

const addAttributeOverride = () => {
  attributeOverrides.value = [...attributeOverrides.value, buildAttributeOverrideDraft()];
};

const addOverrideCondition = (override, groupKey) => {
  const nextGroups = {
    ...(override.condition_groups || { all: [], any: [], not: [] }),
    [groupKey]: [
      ...((override.condition_groups && override.condition_groups[groupKey]) || []),
      buildAttributeOverrideConditionDraft(),
    ],
  };
  override.condition_groups = nextGroups;
};

const removeOverrideCondition = (override, groupKey, conditionId) => {
  const existing = (override.condition_groups && override.condition_groups[groupKey]) || [];
  override.condition_groups = {
    ...(override.condition_groups || { all: [], any: [], not: [] }),
    [groupKey]: existing.filter(condition => condition.id !== conditionId),
  };
};

const removeAttributeOverride = id => {
  attributeOverrides.value = attributeOverrides.value.filter(
    override => override.id !== id
  );
};

const syncFromConfig = config => {
  const promptSections = config?.prompt_sections || {};
  const procedureRouting = promptSections.procedure_routing || {};
  const templateMode = promptSections.template_mode || {};
  const riskPolicy = promptSections.risk_policy || {};
  const playbooks = promptSections.playbooks || {};

  procedureRoutingEnabled.value =
    procedureRouting.enabled === true ||
    String(procedureRouting.enabled || '').trim() === 'true' ||
    playbooks.enabled === true ||
    String(playbooks.enabled || '').trim() === 'true';
  procedureRoutingPrecedence.value =
    ['procedure_before_fallback', 'fallback_before_procedure'].includes(
      String(procedureRouting.routing_precedence || '').trim()
    )
      ? String(procedureRouting.routing_precedence).trim()
      : 'procedure_before_fallback';
  procedureStepConfirmationStrategy.value =
    ['confirm_each_step', 'balanced', 'collect_brief_batch'].includes(
      String(procedureRouting.step_confirmation_strategy || '').trim()
    )
      ? String(procedureRouting.step_confirmation_strategy).trim()
      : 'balanced';
  procedureFallbackAction.value =
    ['ask_clarify', 'default_reply', 'handoff'].includes(
      String(procedureRouting.fallback_action || '').trim()
    )
      ? String(procedureRouting.fallback_action).trim()
      : 'handoff';

  uncertainAction.value =
    ['fallback_ai', 'ask_clarify', 'default_reply'].includes(
      String(templateMode.on_no_match || '').trim()
    )
      ? String(templateMode.on_no_match).trim()
      : 'fallback_ai';
  templateModeEnabled.value = !!templateMode.enabled;
  askClarifyTh.value = (templateMode.ask_clarify_th || '').toString();
  askClarifyEn.value = (templateMode.ask_clarify_en || '').toString();
  defaultReplyTh.value = (templateMode.default_reply_th || '').toString();
  defaultReplyEn.value = (templateMode.default_reply_en || '').toString();

  riskEnabled.value = riskPolicy.enabled !== false;
  
  const tierActions =
    riskPolicy.action_by_tier && typeof riskPolicy.action_by_tier === 'object'
      ? riskPolicy.action_by_tier
      : {};
  highRiskAction.value = tierActions?.L3 || 'auto_suggest';
  attributeOverrides.value = normalizeAttributeOverrideDrafts(
    riskPolicy.attribute_overrides
  );
};

const fetchRuntimeConfig = async () => {
  if (!props.assistant?.id) return;
  try {
    await store.dispatch('aiCloser/fetchConfig', {
      assistantId: props.assistant.id,
    });
  } catch (error) {
    alert(error?.message || 'Failed to load AI escalation config');
  }
};

const fetchRecentEscalations = async () => {
  if (!props.assistant?.id || !accountId.value) return;
  isLoadingRecentEscalations.value = true;
  try {
    const response = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
      {
        params: {
          assistant_id: props.assistant.id,
          decision_type_like: 'response.trace',
          attribute_override_only: true,
          limit: 10,
        },
      }
    );
    recentEscalations.value = response?.data?.data || [];
  } catch (error) {
    recentEscalations.value = [];
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load recent attribute escalations'
    );
  } finally {
    isLoadingRecentEscalations.value = false;
  }
};

const fetchOverrideEffectiveness = async () => {
  if (!props.assistant?.id || !accountId.value) return;
  try {
    const [
      outcomeResponse,
      inboxOutcomeResponse,
      recentCaseResponse,
      conditionOutcomeResponse,
      guardOutcomeResponse,
      conditionRecommendationResponse,
      guardRecommendationResponse,
      recommendationResponse,
    ] = await Promise.all([
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            assistant_id: props.assistant.id,
            decision_type_like: 'response.trace',
            attribute_override_only: true,
            created_after: overrideEffectivenessCreatedAfterIso.value,
            aggregate: 'attribute_override_outcomes',
            limit: 50,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            assistant_id: props.assistant.id,
            decision_type_like: 'response.trace',
            attribute_override_only: true,
            created_after: overrideEffectivenessCreatedAfterIso.value,
            aggregate: 'attribute_override_recent_cases',
            limit: 30,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            assistant_id: props.assistant.id,
            decision_type_like: 'response.trace',
            attribute_override_only: true,
            created_after: overrideEffectivenessCreatedAfterIso.value,
            aggregate: 'attribute_override_condition_outcomes',
            limit: 60,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            assistant_id: props.assistant.id,
            decision_type_like: 'response.trace',
            attribute_override_only: true,
            created_after: overrideEffectivenessCreatedAfterIso.value,
            aggregate: 'attribute_override_guard_outcomes',
            limit: 60,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            assistant_id: props.assistant.id,
            decision_type_like: 'response.trace',
            attribute_override_only: true,
            created_after: overrideEffectivenessCreatedAfterIso.value,
            aggregate: 'attribute_override_condition_recommendations',
            limit: 30,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            assistant_id: props.assistant.id,
            decision_type_like: 'response.trace',
            attribute_override_only: true,
            created_after: overrideEffectivenessCreatedAfterIso.value,
            aggregate: 'attribute_override_guard_recommendations',
            limit: 30,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            assistant_id: props.assistant.id,
            decision_type_like: 'response.trace',
            attribute_override_only: true,
            created_after: overrideEffectivenessCreatedAfterIso.value,
            aggregate: 'attribute_override_inbox_outcomes',
            limit: 50,
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            assistant_id: props.assistant.id,
            decision_type_like: 'response.trace',
            attribute_override_only: true,
            created_after: overrideEffectivenessCreatedAfterIso.value,
            aggregate: 'attribute_override_recommendations',
            limit: 20,
          },
        }
      ),
    ]);
    overrideEffectivenessByRuleIndex.value = (outcomeResponse?.data?.data || []).reduce(
      (acc, row) => {
        const index = Number(row?.key);
        if (!Number.isFinite(index)) return acc;
        const usedCount = Number(row?.used_count || 0);
        const resolvedCount = Number(row?.resolved_count || 0);
        const escalatedCount = Number(row?.escalated_count || 0);
        const unresolvedCount = Number(row?.unresolved_count || 0);
        const topInboxId = Number(row?.top_inbox_id);
        acc[index] = {
          usedCount,
          resolvedCount,
          escalatedCount,
          unresolvedCount,
          resolvedRate: usedCount > 0 ? resolvedCount / usedCount : 0,
          escalatedRate: usedCount > 0 ? escalatedCount / usedCount : 0,
          topInboxId: Number.isFinite(topInboxId) ? topInboxId : null,
          topInboxUsedCount: Number(row?.top_inbox_used_count || 0),
          topInboxResolvedCount: Number(row?.top_inbox_resolved_count || 0),
          topInboxEscalatedCount: Number(row?.top_inbox_escalated_count || 0),
        };
        return acc;
      },
      {}
    );
    overrideInboxBreakdownByRuleIndex.value = (
      inboxOutcomeResponse?.data?.data || []
    ).reduce((acc, row) => {
      const index = Number(row?.rule_index);
      const inboxId = Number(row?.inbox_id);
      if (!Number.isFinite(index) || !Number.isFinite(inboxId)) return acc;
      acc[index] = [
        ...(acc[index] || []),
        {
          key: String(row?.key || '').trim(),
          inboxId,
          usedCount: Number(row?.used_count || 0),
          resolvedCount: Number(row?.resolved_count || 0),
          escalatedCount: Number(row?.escalated_count || 0),
          unresolvedCount: Number(row?.unresolved_count || 0),
        },
      ];
      return acc;
    }, {});
    overrideConditionRollupsByRuleIndex.value = (
      conditionOutcomeResponse?.data?.data || []
    ).reduce((acc, row) => {
      const index = Number(row?.rule_index);
      if (!Number.isFinite(index)) return acc;
      acc[index] = [
        ...(acc[index] || []),
        {
          key: String(row?.key || '').trim(),
          label: String(row?.label || '').trim(),
          usedCount: Number(row?.used_count || 0),
          resolvedCount: Number(row?.resolved_count || 0),
          escalatedCount: Number(row?.escalated_count || 0),
          unresolvedCount: Number(row?.unresolved_count || 0),
          topInboxId: Number(row?.top_inbox_id) || null,
          topInboxUsedCount: Number(row?.top_inbox_used_count || 0),
          topInboxResolvedCount: Number(row?.top_inbox_resolved_count || 0),
          topInboxEscalatedCount: Number(row?.top_inbox_escalated_count || 0),
          topInboxUnresolvedCount: Number(row?.top_inbox_unresolved_count || 0),
          latestInboxId: Number(row?.latest_inbox_id) || null,
          latestConversationId: Number(row?.latest_conversation_id) || null,
          latestDecisionEventId: Number(row?.latest_decision_event_id) || null,
          latestOccurredAt: String(row?.latest_occurred_at || '').trim(),
          latestOutcome: String(row?.latest_outcome || '').trim(),
          latestOutcomeAt: String(row?.latest_outcome_at || '').trim(),
          latestTimeToOutcomeSeconds:
            Number(row?.latest_time_to_outcome_seconds) || 0,
          latestResolvedInboxId: Number(row?.latest_resolved_inbox_id) || null,
          latestResolvedConversationId:
            Number(row?.latest_resolved_conversation_id) || null,
          latestResolvedDecisionEventId:
            Number(row?.latest_resolved_decision_event_id) || null,
          latestResolvedOccurredAt: String(
            row?.latest_resolved_occurred_at || ''
          ).trim(),
          latestResolvedOutcomeAt: String(
            row?.latest_resolved_outcome_at || ''
          ).trim(),
          latestResolvedTimeToOutcomeSeconds:
            Number(row?.latest_resolved_time_to_outcome_seconds) || 0,
          latestEscalatedInboxId:
            Number(row?.latest_escalated_inbox_id) || null,
          latestEscalatedConversationId:
            Number(row?.latest_escalated_conversation_id) || null,
          latestEscalatedDecisionEventId:
            Number(row?.latest_escalated_decision_event_id) || null,
          latestEscalatedOccurredAt: String(
            row?.latest_escalated_occurred_at || ''
          ).trim(),
          latestEscalatedOutcomeAt: String(
            row?.latest_escalated_outcome_at || ''
          ).trim(),
          latestEscalatedTimeToOutcomeSeconds:
            Number(row?.latest_escalated_time_to_outcome_seconds) || 0,
        },
      ];
      return acc;
    }, {});
    overrideGuardRollupsByRuleIndex.value = (
      guardOutcomeResponse?.data?.data || []
    ).reduce((acc, row) => {
      const index = Number(row?.rule_index);
      if (!Number.isFinite(index)) return acc;
      acc[index] = [
        ...(acc[index] || []),
        {
          key: String(row?.key || '').trim(),
          label: String(row?.label || '').trim(),
          usedCount: Number(row?.used_count || 0),
          resolvedCount: Number(row?.resolved_count || 0),
          escalatedCount: Number(row?.escalated_count || 0),
          unresolvedCount: Number(row?.unresolved_count || 0),
          topInboxId: Number(row?.top_inbox_id) || null,
          topInboxUsedCount: Number(row?.top_inbox_used_count || 0),
          topInboxResolvedCount: Number(row?.top_inbox_resolved_count || 0),
          topInboxEscalatedCount: Number(row?.top_inbox_escalated_count || 0),
          topInboxUnresolvedCount: Number(row?.top_inbox_unresolved_count || 0),
          latestInboxId: Number(row?.latest_inbox_id) || null,
          latestConversationId: Number(row?.latest_conversation_id) || null,
          latestDecisionEventId: Number(row?.latest_decision_event_id) || null,
          latestOccurredAt: String(row?.latest_occurred_at || '').trim(),
          latestOutcome: String(row?.latest_outcome || '').trim(),
          latestOutcomeAt: String(row?.latest_outcome_at || '').trim(),
          latestTimeToOutcomeSeconds:
            Number(row?.latest_time_to_outcome_seconds) || 0,
          latestResolvedInboxId: Number(row?.latest_resolved_inbox_id) || null,
          latestResolvedConversationId:
            Number(row?.latest_resolved_conversation_id) || null,
          latestResolvedDecisionEventId:
            Number(row?.latest_resolved_decision_event_id) || null,
          latestResolvedOccurredAt: String(
            row?.latest_resolved_occurred_at || ''
          ).trim(),
          latestResolvedOutcomeAt: String(
            row?.latest_resolved_outcome_at || ''
          ).trim(),
          latestResolvedTimeToOutcomeSeconds:
            Number(row?.latest_resolved_time_to_outcome_seconds) || 0,
          latestEscalatedInboxId:
            Number(row?.latest_escalated_inbox_id) || null,
          latestEscalatedConversationId:
            Number(row?.latest_escalated_conversation_id) || null,
          latestEscalatedDecisionEventId:
            Number(row?.latest_escalated_decision_event_id) || null,
          latestEscalatedOccurredAt: String(
            row?.latest_escalated_occurred_at || ''
          ).trim(),
          latestEscalatedOutcomeAt: String(
            row?.latest_escalated_outcome_at || ''
          ).trim(),
          latestEscalatedTimeToOutcomeSeconds:
            Number(row?.latest_escalated_time_to_outcome_seconds) || 0,
        },
      ];
      return acc;
    }, {});
    overrideConditionRecommendationsByRuleIndex.value = (
      conditionRecommendationResponse?.data?.data || []
    ).reduce((acc, row) => {
      const index = Number(row?.rule_index);
      if (!Number.isFinite(index)) return acc;
      acc[index] = [
        ...(acc[index] || []),
        {
          key: String(row?.key || '').trim(),
          label: String(row?.label || '').trim(),
          recommendationCode: String(row?.recommendation_code || '').trim(),
          title: String(row?.title || '').trim(),
          body: String(row?.body || '').trim(),
          evidenceSummary: String(row?.evidence_summary || '').trim(),
          compareSummary: String(row?.compare_summary || '').trim(),
          compareFocusCode: String(row?.compare_focus_code || '').trim(),
          compareFocusTitle: String(row?.compare_focus_title || '').trim(),
          compareFocus: String(row?.compare_focus || '').trim(),
          compareNextStep: String(row?.compare_next_step || '').trim(),
          fixPresetCode: String(row?.fix_preset_code || '').trim(),
          fixPresetTitle: String(row?.fix_preset_title || '').trim(),
          fixPresetBody: String(row?.fix_preset_body || '').trim(),
          fixPresetDrilldownOutcome: String(
            row?.fix_preset_drilldown_outcome || ''
          ).trim(),
          fixPresetDrilldownInboxId:
            Number(row?.fix_preset_drilldown_inbox_id) || null,
          fixIntentCode: String(row?.fix_intent_code || '').trim(),
          fixIntentTitle: String(row?.fix_intent_title || '').trim(),
          fixIntentSummary: String(row?.fix_intent_summary || '').trim(),
          fixBriefTitle: String(row?.fix_brief_title || '').trim(),
          fixBriefBody: String(row?.fix_brief_body || '').trim(),
          fixBriefValidation: String(row?.fix_brief_validation || '').trim(),
          repairBundleTitle: String(row?.repair_bundle_title || '').trim(),
          repairBundleHotspotSummary: String(
            row?.repair_bundle_hotspot_summary || ''
          ).trim(),
          repairBundleHotspotActionTitle: String(
            row?.repair_bundle_hotspot_action_title || ''
          ).trim(),
          repairBundleHotspotStageCode: String(
            row?.repair_bundle_hotspot_stage_code || ''
          ).trim(),
          repairBundleHotspotStageTitle: String(
            row?.repair_bundle_hotspot_stage_title || ''
          ).trim(),
          repairBundleHotspotStageEvidence: String(
            row?.repair_bundle_hotspot_stage_evidence || ''
          ).trim(),
          repairBundleHotspotStageFocus: String(
            row?.repair_bundle_hotspot_stage_focus || ''
          ).trim(),
          repairBundleHotspotStageDecision: String(
            row?.repair_bundle_hotspot_stage_decision || ''
          ).trim(),
          repairBundleHotspotStageRepairMove: String(
            row?.repair_bundle_hotspot_stage_repair_move || ''
          ).trim(),
          repairBundleHotspotStageWatchpoint: String(
            row?.repair_bundle_hotspot_stage_watchpoint || ''
          ).trim(),
          repairBundleHotspotStageExecutionBrief: String(
            row?.repair_bundle_hotspot_stage_execution_brief || ''
          ).trim(),
          repairBundleHotspotStageValidationFocus: String(
            row?.repair_bundle_hotspot_stage_validation_focus || ''
          ).trim(),
          repairBundleHotspotStageOwnerPacketTitle: String(
            row?.repair_bundle_hotspot_stage_owner_packet_title || ''
          ).trim(),
          repairBundleHotspotStageOwnerPacketBody: String(
            row?.repair_bundle_hotspot_stage_owner_packet_body || ''
          ).trim(),
          repairBundleHotspotStageOwnerRole: String(
            row?.repair_bundle_hotspot_stage_owner_role || ''
          ).trim(),
          repairBundleHotspotStageHandoffTarget: String(
            row?.repair_bundle_hotspot_stage_handoff_target || ''
          ).trim(),
          repairBundleHotspotStageSuccessCheck: String(
            row?.repair_bundle_hotspot_stage_success_check || ''
          ).trim(),
          repairBundleHotspotStageLedgerStatus: String(
            row?.repair_bundle_hotspot_stage_ledger_status || ''
          ).trim(),
          repairBundleHotspotStageLastAction: String(
            row?.repair_bundle_hotspot_stage_last_action || ''
          ).trim(),
          repairBundleHotspotStageReopenRule: String(
            row?.repair_bundle_hotspot_stage_reopen_rule || ''
          ).trim(),
          repairBundleHotspotStageNextOwner: String(
            row?.repair_bundle_hotspot_stage_next_owner || ''
          ).trim(),
          repairBundleHotspotStageBlockedReason: String(
            row?.repair_bundle_hotspot_stage_blocked_reason || ''
          ).trim(),
          repairBundleHotspotStageCompletionGate: String(
            row?.repair_bundle_hotspot_stage_completion_gate || ''
          ).trim(),
          repairBundleHotspotStageDoneState: String(
            row?.repair_bundle_hotspot_stage_done_state || ''
          ).trim(),
          repairBundleHotspotStageFailState: String(
            row?.repair_bundle_hotspot_stage_fail_state || ''
          ).trim(),
          repairBundleHotspotStageRollbackTrigger: String(
            row?.repair_bundle_hotspot_stage_rollback_trigger || ''
          ).trim(),
          repairBundleHotspotNextStageCode: String(
            row?.repair_bundle_hotspot_next_stage_code || ''
          ).trim(),
          repairBundleHotspotNextStageTitle: String(
            row?.repair_bundle_hotspot_next_stage_title || ''
          ).trim(),
          repairBundleHotspotActionInboxId:
            Number(row?.repair_bundle_hotspot_action_inbox_id) || null,
          repairBundleResolvedSummary: String(
            row?.repair_bundle_resolved_summary || ''
          ).trim(),
          repairBundleResolvedActionTitle: String(
            row?.repair_bundle_resolved_action_title || ''
          ).trim(),
          repairBundleResolvedStageCode: String(
            row?.repair_bundle_resolved_stage_code || ''
          ).trim(),
          repairBundleResolvedStageTitle: String(
            row?.repair_bundle_resolved_stage_title || ''
          ).trim(),
          repairBundleResolvedStageEvidence: String(
            row?.repair_bundle_resolved_stage_evidence || ''
          ).trim(),
          repairBundleResolvedStageFocus: String(
            row?.repair_bundle_resolved_stage_focus || ''
          ).trim(),
          repairBundleResolvedStageDecision: String(
            row?.repair_bundle_resolved_stage_decision || ''
          ).trim(),
          repairBundleResolvedStageRepairMove: String(
            row?.repair_bundle_resolved_stage_repair_move || ''
          ).trim(),
          repairBundleResolvedStageWatchpoint: String(
            row?.repair_bundle_resolved_stage_watchpoint || ''
          ).trim(),
          repairBundleResolvedStageExecutionBrief: String(
            row?.repair_bundle_resolved_stage_execution_brief || ''
          ).trim(),
          repairBundleResolvedStageValidationFocus: String(
            row?.repair_bundle_resolved_stage_validation_focus || ''
          ).trim(),
          repairBundleResolvedStageOwnerPacketTitle: String(
            row?.repair_bundle_resolved_stage_owner_packet_title || ''
          ).trim(),
          repairBundleResolvedStageOwnerPacketBody: String(
            row?.repair_bundle_resolved_stage_owner_packet_body || ''
          ).trim(),
          repairBundleResolvedStageOwnerRole: String(
            row?.repair_bundle_resolved_stage_owner_role || ''
          ).trim(),
          repairBundleResolvedStageHandoffTarget: String(
            row?.repair_bundle_resolved_stage_handoff_target || ''
          ).trim(),
          repairBundleResolvedStageSuccessCheck: String(
            row?.repair_bundle_resolved_stage_success_check || ''
          ).trim(),
          repairBundleResolvedStageLedgerStatus: String(
            row?.repair_bundle_resolved_stage_ledger_status || ''
          ).trim(),
          repairBundleResolvedStageLastAction: String(
            row?.repair_bundle_resolved_stage_last_action || ''
          ).trim(),
          repairBundleResolvedStageReopenRule: String(
            row?.repair_bundle_resolved_stage_reopen_rule || ''
          ).trim(),
          repairBundleResolvedStageNextOwner: String(
            row?.repair_bundle_resolved_stage_next_owner || ''
          ).trim(),
          repairBundleResolvedStageBlockedReason: String(
            row?.repair_bundle_resolved_stage_blocked_reason || ''
          ).trim(),
          repairBundleResolvedStageCompletionGate: String(
            row?.repair_bundle_resolved_stage_completion_gate || ''
          ).trim(),
          repairBundleResolvedStageDoneState: String(
            row?.repair_bundle_resolved_stage_done_state || ''
          ).trim(),
          repairBundleResolvedStageFailState: String(
            row?.repair_bundle_resolved_stage_fail_state || ''
          ).trim(),
          repairBundleResolvedStageRollbackTrigger: String(
            row?.repair_bundle_resolved_stage_rollback_trigger || ''
          ).trim(),
          repairBundleResolvedNextStageCode: String(
            row?.repair_bundle_resolved_next_stage_code || ''
          ).trim(),
          repairBundleResolvedNextStageTitle: String(
            row?.repair_bundle_resolved_next_stage_title || ''
          ).trim(),
          repairBundleResolvedActionInboxId:
            Number(row?.repair_bundle_resolved_action_inbox_id) || null,
          repairBundleResolvedActionConversationId:
            Number(row?.repair_bundle_resolved_action_conversation_id) || null,
          repairBundleResolvedActionOutcome: String(
            row?.repair_bundle_resolved_action_outcome || ''
          ).trim(),
          repairBundleEscalatedSummary: String(
            row?.repair_bundle_escalated_summary || ''
          ).trim(),
          repairBundleEscalatedActionTitle: String(
            row?.repair_bundle_escalated_action_title || ''
          ).trim(),
          repairBundleEscalatedStageCode: String(
            row?.repair_bundle_escalated_stage_code || ''
          ).trim(),
          repairBundleEscalatedStageTitle: String(
            row?.repair_bundle_escalated_stage_title || ''
          ).trim(),
          repairBundleEscalatedStageEvidence: String(
            row?.repair_bundle_escalated_stage_evidence || ''
          ).trim(),
          repairBundleEscalatedStageFocus: String(
            row?.repair_bundle_escalated_stage_focus || ''
          ).trim(),
          repairBundleEscalatedStageDecision: String(
            row?.repair_bundle_escalated_stage_decision || ''
          ).trim(),
          repairBundleEscalatedStageRepairMove: String(
            row?.repair_bundle_escalated_stage_repair_move || ''
          ).trim(),
          repairBundleEscalatedStageWatchpoint: String(
            row?.repair_bundle_escalated_stage_watchpoint || ''
          ).trim(),
          repairBundleEscalatedStageExecutionBrief: String(
            row?.repair_bundle_escalated_stage_execution_brief || ''
          ).trim(),
          repairBundleEscalatedStageValidationFocus: String(
            row?.repair_bundle_escalated_stage_validation_focus || ''
          ).trim(),
          repairBundleEscalatedStageOwnerPacketTitle: String(
            row?.repair_bundle_escalated_stage_owner_packet_title || ''
          ).trim(),
          repairBundleEscalatedStageOwnerPacketBody: String(
            row?.repair_bundle_escalated_stage_owner_packet_body || ''
          ).trim(),
          repairBundleEscalatedStageOwnerRole: String(
            row?.repair_bundle_escalated_stage_owner_role || ''
          ).trim(),
          repairBundleEscalatedStageHandoffTarget: String(
            row?.repair_bundle_escalated_stage_handoff_target || ''
          ).trim(),
          repairBundleEscalatedStageSuccessCheck: String(
            row?.repair_bundle_escalated_stage_success_check || ''
          ).trim(),
          repairBundleEscalatedStageLedgerStatus: String(
            row?.repair_bundle_escalated_stage_ledger_status || ''
          ).trim(),
          repairBundleEscalatedStageLastAction: String(
            row?.repair_bundle_escalated_stage_last_action || ''
          ).trim(),
          repairBundleEscalatedStageReopenRule: String(
            row?.repair_bundle_escalated_stage_reopen_rule || ''
          ).trim(),
          repairBundleEscalatedStageNextOwner: String(
            row?.repair_bundle_escalated_stage_next_owner || ''
          ).trim(),
          repairBundleEscalatedStageBlockedReason: String(
            row?.repair_bundle_escalated_stage_blocked_reason || ''
          ).trim(),
          repairBundleEscalatedStageCompletionGate: String(
            row?.repair_bundle_escalated_stage_completion_gate || ''
          ).trim(),
          repairBundleEscalatedStageDoneState: String(
            row?.repair_bundle_escalated_stage_done_state || ''
          ).trim(),
          repairBundleEscalatedStageFailState: String(
            row?.repair_bundle_escalated_stage_fail_state || ''
          ).trim(),
          repairBundleEscalatedStageRollbackTrigger: String(
            row?.repair_bundle_escalated_stage_rollback_trigger || ''
          ).trim(),
          repairBundleEscalatedNextStageCode: String(
            row?.repair_bundle_escalated_next_stage_code || ''
          ).trim(),
          repairBundleEscalatedNextStageTitle: String(
            row?.repair_bundle_escalated_next_stage_title || ''
          ).trim(),
          repairBundleEscalatedActionInboxId:
            Number(row?.repair_bundle_escalated_action_inbox_id) || null,
          repairBundleEscalatedActionConversationId:
            Number(row?.repair_bundle_escalated_action_conversation_id) || null,
          repairBundleEscalatedActionOutcome: String(
            row?.repair_bundle_escalated_action_outcome || ''
          ).trim(),
          repairBundleValidationSummary: String(
            row?.repair_bundle_validation_summary || ''
          ).trim(),
          repairBundleValidationActionTitle: String(
            row?.repair_bundle_validation_action_title || ''
          ).trim(),
          repairBundleValidationStageCode: String(
            row?.repair_bundle_validation_stage_code || ''
          ).trim(),
          repairBundleValidationStageTitle: String(
            row?.repair_bundle_validation_stage_title || ''
          ).trim(),
          repairBundleValidationStageEvidence: String(
            row?.repair_bundle_validation_stage_evidence || ''
          ).trim(),
          repairBundleValidationStageFocus: String(
            row?.repair_bundle_validation_stage_focus || ''
          ).trim(),
          repairBundleValidationStageDecision: String(
            row?.repair_bundle_validation_stage_decision || ''
          ).trim(),
          repairBundleValidationStageRepairMove: String(
            row?.repair_bundle_validation_stage_repair_move || ''
          ).trim(),
          repairBundleValidationStageWatchpoint: String(
            row?.repair_bundle_validation_stage_watchpoint || ''
          ).trim(),
          repairBundleValidationStageExecutionBrief: String(
            row?.repair_bundle_validation_stage_execution_brief || ''
          ).trim(),
          repairBundleValidationStageValidationFocus: String(
            row?.repair_bundle_validation_stage_validation_focus || ''
          ).trim(),
          repairBundleValidationStageOwnerPacketTitle: String(
            row?.repair_bundle_validation_stage_owner_packet_title || ''
          ).trim(),
          repairBundleValidationStageOwnerPacketBody: String(
            row?.repair_bundle_validation_stage_owner_packet_body || ''
          ).trim(),
          repairBundleValidationStageOwnerRole: String(
            row?.repair_bundle_validation_stage_owner_role || ''
          ).trim(),
          repairBundleValidationStageHandoffTarget: String(
            row?.repair_bundle_validation_stage_handoff_target || ''
          ).trim(),
          repairBundleValidationStageSuccessCheck: String(
            row?.repair_bundle_validation_stage_success_check || ''
          ).trim(),
          repairBundleValidationStageLedgerStatus: String(
            row?.repair_bundle_validation_stage_ledger_status || ''
          ).trim(),
          repairBundleValidationStageLastAction: String(
            row?.repair_bundle_validation_stage_last_action || ''
          ).trim(),
          repairBundleValidationStageReopenRule: String(
            row?.repair_bundle_validation_stage_reopen_rule || ''
          ).trim(),
          repairBundleValidationStageNextOwner: String(
            row?.repair_bundle_validation_stage_next_owner || ''
          ).trim(),
          repairBundleValidationStageBlockedReason: String(
            row?.repair_bundle_validation_stage_blocked_reason || ''
          ).trim(),
          repairBundleValidationStageCompletionGate: String(
            row?.repair_bundle_validation_stage_completion_gate || ''
          ).trim(),
          repairBundleValidationStageDoneState: String(
            row?.repair_bundle_validation_stage_done_state || ''
          ).trim(),
          repairBundleValidationStageFailState: String(
            row?.repair_bundle_validation_stage_fail_state || ''
          ).trim(),
          repairBundleValidationStageRollbackTrigger: String(
            row?.repair_bundle_validation_stage_rollback_trigger || ''
          ).trim(),
          repairBundleValidationNextStageCode: String(
            row?.repair_bundle_validation_next_stage_code || ''
          ).trim(),
          repairBundleValidationNextStageTitle: String(
            row?.repair_bundle_validation_next_stage_title || ''
          ).trim(),
          repairBundleValidationActionInboxId:
            Number(row?.repair_bundle_validation_action_inbox_id) || null,
          repairBundleValidationActionOutcome: String(
            row?.repair_bundle_validation_action_outcome || ''
          ).trim(),
          suggestedChange: String(row?.suggested_change || '').trim(),
          nextStep: String(row?.next_step || '').trim(),
          severity: String(row?.severity || '').trim(),
          topInboxId: Number(row?.top_inbox_id) || null,
          latestInboxId: Number(row?.latest_inbox_id) || null,
          latestConversationId: Number(row?.latest_conversation_id) || null,
          latestOutcome: String(row?.latest_outcome || '').trim(),
          latestResolvedInboxId: Number(row?.latest_resolved_inbox_id) || null,
          latestResolvedConversationId:
            Number(row?.latest_resolved_conversation_id) || null,
          latestResolvedOccurredAt: String(
            row?.latest_resolved_occurred_at || ''
          ).trim(),
          latestResolvedOutcomeAt: String(
            row?.latest_resolved_outcome_at || ''
          ).trim(),
          latestResolvedTimeToOutcomeSeconds:
            Number(row?.latest_resolved_time_to_outcome_seconds) || 0,
          latestEscalatedInboxId:
            Number(row?.latest_escalated_inbox_id) || null,
          latestEscalatedConversationId:
            Number(row?.latest_escalated_conversation_id) || null,
          latestEscalatedOccurredAt: String(
            row?.latest_escalated_occurred_at || ''
          ).trim(),
          latestEscalatedOutcomeAt: String(
            row?.latest_escalated_outcome_at || ''
          ).trim(),
          latestEscalatedTimeToOutcomeSeconds:
            Number(row?.latest_escalated_time_to_outcome_seconds) || 0,
        },
      ];
      return acc;
    }, {});
    overrideGuardRecommendationsByRuleIndex.value = (
      guardRecommendationResponse?.data?.data || []
    ).reduce((acc, row) => {
      const index = Number(row?.rule_index);
      if (!Number.isFinite(index)) return acc;
      acc[index] = [
        ...(acc[index] || []),
        {
          key: String(row?.key || '').trim(),
          label: String(row?.label || '').trim(),
          recommendationCode: String(row?.recommendation_code || '').trim(),
          title: String(row?.title || '').trim(),
          body: String(row?.body || '').trim(),
          evidenceSummary: String(row?.evidence_summary || '').trim(),
          compareSummary: String(row?.compare_summary || '').trim(),
          compareFocusCode: String(row?.compare_focus_code || '').trim(),
          compareFocusTitle: String(row?.compare_focus_title || '').trim(),
          compareFocus: String(row?.compare_focus || '').trim(),
          compareNextStep: String(row?.compare_next_step || '').trim(),
          fixPresetCode: String(row?.fix_preset_code || '').trim(),
          fixPresetTitle: String(row?.fix_preset_title || '').trim(),
          fixPresetBody: String(row?.fix_preset_body || '').trim(),
          fixPresetDrilldownOutcome: String(
            row?.fix_preset_drilldown_outcome || ''
          ).trim(),
          fixPresetDrilldownInboxId:
            Number(row?.fix_preset_drilldown_inbox_id) || null,
          fixIntentCode: String(row?.fix_intent_code || '').trim(),
          fixIntentTitle: String(row?.fix_intent_title || '').trim(),
          fixIntentSummary: String(row?.fix_intent_summary || '').trim(),
          fixBriefTitle: String(row?.fix_brief_title || '').trim(),
          fixBriefBody: String(row?.fix_brief_body || '').trim(),
          fixBriefValidation: String(row?.fix_brief_validation || '').trim(),
          repairBundleTitle: String(row?.repair_bundle_title || '').trim(),
          repairBundleHotspotSummary: String(
            row?.repair_bundle_hotspot_summary || ''
          ).trim(),
          repairBundleHotspotActionTitle: String(
            row?.repair_bundle_hotspot_action_title || ''
          ).trim(),
          repairBundleHotspotStageCode: String(
            row?.repair_bundle_hotspot_stage_code || ''
          ).trim(),
          repairBundleHotspotStageTitle: String(
            row?.repair_bundle_hotspot_stage_title || ''
          ).trim(),
          repairBundleHotspotStageEvidence: String(
            row?.repair_bundle_hotspot_stage_evidence || ''
          ).trim(),
          repairBundleHotspotStageFocus: String(
            row?.repair_bundle_hotspot_stage_focus || ''
          ).trim(),
          repairBundleHotspotStageDecision: String(
            row?.repair_bundle_hotspot_stage_decision || ''
          ).trim(),
          repairBundleHotspotStageRepairMove: String(
            row?.repair_bundle_hotspot_stage_repair_move || ''
          ).trim(),
          repairBundleHotspotStageWatchpoint: String(
            row?.repair_bundle_hotspot_stage_watchpoint || ''
          ).trim(),
          repairBundleHotspotStageExecutionBrief: String(
            row?.repair_bundle_hotspot_stage_execution_brief || ''
          ).trim(),
          repairBundleHotspotStageValidationFocus: String(
            row?.repair_bundle_hotspot_stage_validation_focus || ''
          ).trim(),
          repairBundleHotspotStageOwnerPacketTitle: String(
            row?.repair_bundle_hotspot_stage_owner_packet_title || ''
          ).trim(),
          repairBundleHotspotStageOwnerPacketBody: String(
            row?.repair_bundle_hotspot_stage_owner_packet_body || ''
          ).trim(),
          repairBundleHotspotStageOwnerRole: String(
            row?.repair_bundle_hotspot_stage_owner_role || ''
          ).trim(),
          repairBundleHotspotStageHandoffTarget: String(
            row?.repair_bundle_hotspot_stage_handoff_target || ''
          ).trim(),
          repairBundleHotspotStageSuccessCheck: String(
            row?.repair_bundle_hotspot_stage_success_check || ''
          ).trim(),
          repairBundleHotspotStageLedgerStatus: String(
            row?.repair_bundle_hotspot_stage_ledger_status || ''
          ).trim(),
          repairBundleHotspotStageLastAction: String(
            row?.repair_bundle_hotspot_stage_last_action || ''
          ).trim(),
          repairBundleHotspotStageReopenRule: String(
            row?.repair_bundle_hotspot_stage_reopen_rule || ''
          ).trim(),
          repairBundleHotspotStageNextOwner: String(
            row?.repair_bundle_hotspot_stage_next_owner || ''
          ).trim(),
          repairBundleHotspotStageBlockedReason: String(
            row?.repair_bundle_hotspot_stage_blocked_reason || ''
          ).trim(),
          repairBundleHotspotStageCompletionGate: String(
            row?.repair_bundle_hotspot_stage_completion_gate || ''
          ).trim(),
          repairBundleHotspotStageDoneState: String(
            row?.repair_bundle_hotspot_stage_done_state || ''
          ).trim(),
          repairBundleHotspotStageFailState: String(
            row?.repair_bundle_hotspot_stage_fail_state || ''
          ).trim(),
          repairBundleHotspotStageRollbackTrigger: String(
            row?.repair_bundle_hotspot_stage_rollback_trigger || ''
          ).trim(),
          repairBundleHotspotNextStageCode: String(
            row?.repair_bundle_hotspot_next_stage_code || ''
          ).trim(),
          repairBundleHotspotNextStageTitle: String(
            row?.repair_bundle_hotspot_next_stage_title || ''
          ).trim(),
          repairBundleHotspotActionInboxId:
            Number(row?.repair_bundle_hotspot_action_inbox_id) || null,
          repairBundleResolvedSummary: String(
            row?.repair_bundle_resolved_summary || ''
          ).trim(),
          repairBundleResolvedActionTitle: String(
            row?.repair_bundle_resolved_action_title || ''
          ).trim(),
          repairBundleResolvedStageCode: String(
            row?.repair_bundle_resolved_stage_code || ''
          ).trim(),
          repairBundleResolvedStageTitle: String(
            row?.repair_bundle_resolved_stage_title || ''
          ).trim(),
          repairBundleResolvedStageEvidence: String(
            row?.repair_bundle_resolved_stage_evidence || ''
          ).trim(),
          repairBundleResolvedStageFocus: String(
            row?.repair_bundle_resolved_stage_focus || ''
          ).trim(),
          repairBundleResolvedStageDecision: String(
            row?.repair_bundle_resolved_stage_decision || ''
          ).trim(),
          repairBundleResolvedStageRepairMove: String(
            row?.repair_bundle_resolved_stage_repair_move || ''
          ).trim(),
          repairBundleResolvedStageWatchpoint: String(
            row?.repair_bundle_resolved_stage_watchpoint || ''
          ).trim(),
          repairBundleResolvedStageExecutionBrief: String(
            row?.repair_bundle_resolved_stage_execution_brief || ''
          ).trim(),
          repairBundleResolvedStageValidationFocus: String(
            row?.repair_bundle_resolved_stage_validation_focus || ''
          ).trim(),
          repairBundleResolvedStageOwnerPacketTitle: String(
            row?.repair_bundle_resolved_stage_owner_packet_title || ''
          ).trim(),
          repairBundleResolvedStageOwnerPacketBody: String(
            row?.repair_bundle_resolved_stage_owner_packet_body || ''
          ).trim(),
          repairBundleResolvedStageOwnerRole: String(
            row?.repair_bundle_resolved_stage_owner_role || ''
          ).trim(),
          repairBundleResolvedStageHandoffTarget: String(
            row?.repair_bundle_resolved_stage_handoff_target || ''
          ).trim(),
          repairBundleResolvedStageSuccessCheck: String(
            row?.repair_bundle_resolved_stage_success_check || ''
          ).trim(),
          repairBundleResolvedStageLedgerStatus: String(
            row?.repair_bundle_resolved_stage_ledger_status || ''
          ).trim(),
          repairBundleResolvedStageLastAction: String(
            row?.repair_bundle_resolved_stage_last_action || ''
          ).trim(),
          repairBundleResolvedStageReopenRule: String(
            row?.repair_bundle_resolved_stage_reopen_rule || ''
          ).trim(),
          repairBundleResolvedStageNextOwner: String(
            row?.repair_bundle_resolved_stage_next_owner || ''
          ).trim(),
          repairBundleResolvedStageBlockedReason: String(
            row?.repair_bundle_resolved_stage_blocked_reason || ''
          ).trim(),
          repairBundleResolvedStageCompletionGate: String(
            row?.repair_bundle_resolved_stage_completion_gate || ''
          ).trim(),
          repairBundleResolvedStageDoneState: String(
            row?.repair_bundle_resolved_stage_done_state || ''
          ).trim(),
          repairBundleResolvedStageFailState: String(
            row?.repair_bundle_resolved_stage_fail_state || ''
          ).trim(),
          repairBundleResolvedStageRollbackTrigger: String(
            row?.repair_bundle_resolved_stage_rollback_trigger || ''
          ).trim(),
          repairBundleResolvedNextStageCode: String(
            row?.repair_bundle_resolved_next_stage_code || ''
          ).trim(),
          repairBundleResolvedNextStageTitle: String(
            row?.repair_bundle_resolved_next_stage_title || ''
          ).trim(),
          repairBundleResolvedActionInboxId:
            Number(row?.repair_bundle_resolved_action_inbox_id) || null,
          repairBundleResolvedActionConversationId:
            Number(row?.repair_bundle_resolved_action_conversation_id) || null,
          repairBundleResolvedActionOutcome: String(
            row?.repair_bundle_resolved_action_outcome || ''
          ).trim(),
          repairBundleEscalatedSummary: String(
            row?.repair_bundle_escalated_summary || ''
          ).trim(),
          repairBundleEscalatedActionTitle: String(
            row?.repair_bundle_escalated_action_title || ''
          ).trim(),
          repairBundleEscalatedStageCode: String(
            row?.repair_bundle_escalated_stage_code || ''
          ).trim(),
          repairBundleEscalatedStageTitle: String(
            row?.repair_bundle_escalated_stage_title || ''
          ).trim(),
          repairBundleEscalatedStageEvidence: String(
            row?.repair_bundle_escalated_stage_evidence || ''
          ).trim(),
          repairBundleEscalatedStageFocus: String(
            row?.repair_bundle_escalated_stage_focus || ''
          ).trim(),
          repairBundleEscalatedStageDecision: String(
            row?.repair_bundle_escalated_stage_decision || ''
          ).trim(),
          repairBundleEscalatedStageRepairMove: String(
            row?.repair_bundle_escalated_stage_repair_move || ''
          ).trim(),
          repairBundleEscalatedStageWatchpoint: String(
            row?.repair_bundle_escalated_stage_watchpoint || ''
          ).trim(),
          repairBundleEscalatedStageExecutionBrief: String(
            row?.repair_bundle_escalated_stage_execution_brief || ''
          ).trim(),
          repairBundleEscalatedStageValidationFocus: String(
            row?.repair_bundle_escalated_stage_validation_focus || ''
          ).trim(),
          repairBundleEscalatedStageOwnerPacketTitle: String(
            row?.repair_bundle_escalated_stage_owner_packet_title || ''
          ).trim(),
          repairBundleEscalatedStageOwnerPacketBody: String(
            row?.repair_bundle_escalated_stage_owner_packet_body || ''
          ).trim(),
          repairBundleEscalatedStageOwnerRole: String(
            row?.repair_bundle_escalated_stage_owner_role || ''
          ).trim(),
          repairBundleEscalatedStageHandoffTarget: String(
            row?.repair_bundle_escalated_stage_handoff_target || ''
          ).trim(),
          repairBundleEscalatedStageSuccessCheck: String(
            row?.repair_bundle_escalated_stage_success_check || ''
          ).trim(),
          repairBundleEscalatedStageLedgerStatus: String(
            row?.repair_bundle_escalated_stage_ledger_status || ''
          ).trim(),
          repairBundleEscalatedStageLastAction: String(
            row?.repair_bundle_escalated_stage_last_action || ''
          ).trim(),
          repairBundleEscalatedStageReopenRule: String(
            row?.repair_bundle_escalated_stage_reopen_rule || ''
          ).trim(),
          repairBundleEscalatedStageNextOwner: String(
            row?.repair_bundle_escalated_stage_next_owner || ''
          ).trim(),
          repairBundleEscalatedStageBlockedReason: String(
            row?.repair_bundle_escalated_stage_blocked_reason || ''
          ).trim(),
          repairBundleEscalatedStageCompletionGate: String(
            row?.repair_bundle_escalated_stage_completion_gate || ''
          ).trim(),
          repairBundleEscalatedStageDoneState: String(
            row?.repair_bundle_escalated_stage_done_state || ''
          ).trim(),
          repairBundleEscalatedStageFailState: String(
            row?.repair_bundle_escalated_stage_fail_state || ''
          ).trim(),
          repairBundleEscalatedStageRollbackTrigger: String(
            row?.repair_bundle_escalated_stage_rollback_trigger || ''
          ).trim(),
          repairBundleEscalatedNextStageCode: String(
            row?.repair_bundle_escalated_next_stage_code || ''
          ).trim(),
          repairBundleEscalatedNextStageTitle: String(
            row?.repair_bundle_escalated_next_stage_title || ''
          ).trim(),
          repairBundleEscalatedActionInboxId:
            Number(row?.repair_bundle_escalated_action_inbox_id) || null,
          repairBundleEscalatedActionConversationId:
            Number(row?.repair_bundle_escalated_action_conversation_id) || null,
          repairBundleEscalatedActionOutcome: String(
            row?.repair_bundle_escalated_action_outcome || ''
          ).trim(),
          repairBundleValidationSummary: String(
            row?.repair_bundle_validation_summary || ''
          ).trim(),
          repairBundleValidationActionTitle: String(
            row?.repair_bundle_validation_action_title || ''
          ).trim(),
          repairBundleValidationStageCode: String(
            row?.repair_bundle_validation_stage_code || ''
          ).trim(),
          repairBundleValidationStageTitle: String(
            row?.repair_bundle_validation_stage_title || ''
          ).trim(),
          repairBundleValidationStageEvidence: String(
            row?.repair_bundle_validation_stage_evidence || ''
          ).trim(),
          repairBundleValidationStageFocus: String(
            row?.repair_bundle_validation_stage_focus || ''
          ).trim(),
          repairBundleValidationStageDecision: String(
            row?.repair_bundle_validation_stage_decision || ''
          ).trim(),
          repairBundleValidationStageRepairMove: String(
            row?.repair_bundle_validation_stage_repair_move || ''
          ).trim(),
          repairBundleValidationStageWatchpoint: String(
            row?.repair_bundle_validation_stage_watchpoint || ''
          ).trim(),
          repairBundleValidationStageExecutionBrief: String(
            row?.repair_bundle_validation_stage_execution_brief || ''
          ).trim(),
          repairBundleValidationStageValidationFocus: String(
            row?.repair_bundle_validation_stage_validation_focus || ''
          ).trim(),
          repairBundleValidationStageOwnerPacketTitle: String(
            row?.repair_bundle_validation_stage_owner_packet_title || ''
          ).trim(),
          repairBundleValidationStageOwnerPacketBody: String(
            row?.repair_bundle_validation_stage_owner_packet_body || ''
          ).trim(),
          repairBundleValidationStageOwnerRole: String(
            row?.repair_bundle_validation_stage_owner_role || ''
          ).trim(),
          repairBundleValidationStageHandoffTarget: String(
            row?.repair_bundle_validation_stage_handoff_target || ''
          ).trim(),
          repairBundleValidationStageSuccessCheck: String(
            row?.repair_bundle_validation_stage_success_check || ''
          ).trim(),
          repairBundleValidationStageLedgerStatus: String(
            row?.repair_bundle_validation_stage_ledger_status || ''
          ).trim(),
          repairBundleValidationStageLastAction: String(
            row?.repair_bundle_validation_stage_last_action || ''
          ).trim(),
          repairBundleValidationStageReopenRule: String(
            row?.repair_bundle_validation_stage_reopen_rule || ''
          ).trim(),
          repairBundleValidationStageNextOwner: String(
            row?.repair_bundle_validation_stage_next_owner || ''
          ).trim(),
          repairBundleValidationStageBlockedReason: String(
            row?.repair_bundle_validation_stage_blocked_reason || ''
          ).trim(),
          repairBundleValidationStageCompletionGate: String(
            row?.repair_bundle_validation_stage_completion_gate || ''
          ).trim(),
          repairBundleValidationStageDoneState: String(
            row?.repair_bundle_validation_stage_done_state || ''
          ).trim(),
          repairBundleValidationStageFailState: String(
            row?.repair_bundle_validation_stage_fail_state || ''
          ).trim(),
          repairBundleValidationStageRollbackTrigger: String(
            row?.repair_bundle_validation_stage_rollback_trigger || ''
          ).trim(),
          repairBundleValidationNextStageCode: String(
            row?.repair_bundle_validation_next_stage_code || ''
          ).trim(),
          repairBundleValidationNextStageTitle: String(
            row?.repair_bundle_validation_next_stage_title || ''
          ).trim(),
          repairBundleValidationActionInboxId:
            Number(row?.repair_bundle_validation_action_inbox_id) || null,
          repairBundleValidationActionOutcome: String(
            row?.repair_bundle_validation_action_outcome || ''
          ).trim(),
          suggestedChange: String(row?.suggested_change || '').trim(),
          nextStep: String(row?.next_step || '').trim(),
          severity: String(row?.severity || '').trim(),
          topInboxId: Number(row?.top_inbox_id) || null,
          latestInboxId: Number(row?.latest_inbox_id) || null,
          latestConversationId: Number(row?.latest_conversation_id) || null,
          latestOutcome: String(row?.latest_outcome || '').trim(),
          latestResolvedInboxId: Number(row?.latest_resolved_inbox_id) || null,
          latestResolvedConversationId:
            Number(row?.latest_resolved_conversation_id) || null,
          latestResolvedOccurredAt: String(
            row?.latest_resolved_occurred_at || ''
          ).trim(),
          latestResolvedOutcomeAt: String(
            row?.latest_resolved_outcome_at || ''
          ).trim(),
          latestResolvedTimeToOutcomeSeconds:
            Number(row?.latest_resolved_time_to_outcome_seconds) || 0,
          latestEscalatedInboxId:
            Number(row?.latest_escalated_inbox_id) || null,
          latestEscalatedConversationId:
            Number(row?.latest_escalated_conversation_id) || null,
          latestEscalatedOccurredAt: String(
            row?.latest_escalated_occurred_at || ''
          ).trim(),
          latestEscalatedOutcomeAt: String(
            row?.latest_escalated_outcome_at || ''
          ).trim(),
          latestEscalatedTimeToOutcomeSeconds:
            Number(row?.latest_escalated_time_to_outcome_seconds) || 0,
        },
      ];
      return acc;
    }, {});
    overrideRecentCasesByRuleIndex.value = (
      recentCaseResponse?.data?.data || []
    ).reduce((acc, row) => {
      const index = Number(row?.rule_index);
      const inboxId = Number(row?.inbox_id);
      const conversationId = Number(row?.conversation_id);
      const decisionEventId = Number(row?.decision_event_id);
      if (!Number.isFinite(index)) return acc;
      acc[index] = [
        ...(acc[index] || []),
        {
          key: String(row?.key || '').trim(),
          inboxId: Number.isFinite(inboxId) ? inboxId : null,
          conversationId: Number.isFinite(conversationId) ? conversationId : null,
          decisionEventId: Number.isFinite(decisionEventId) ? decisionEventId : null,
          matchedConditionKeys: Array.isArray(row?.matched_condition_keys)
            ? row.matched_condition_keys.map(value => String(value || '').trim()).filter(Boolean)
            : [],
          matchedConditionSummaries: Array.isArray(row?.matched_condition_summaries)
            ? row.matched_condition_summaries.map(value => String(value || '').trim()).filter(Boolean)
            : [],
          passedGuardKeys: Array.isArray(row?.passed_guard_keys)
            ? row.passed_guard_keys.map(value => String(value || '').trim()).filter(Boolean)
            : [],
          passedGuardSummaries: Array.isArray(row?.passed_guard_summaries)
            ? row.passed_guard_summaries.map(value => String(value || '').trim()).filter(Boolean)
            : [],
          outcome: String(row?.outcome || 'pending').trim() || 'pending',
          occurredAt: String(row?.occurred_at || '').trim(),
          outcomeAt: String(row?.outcome_at || '').trim(),
          timeToOutcomeSeconds: Number(row?.time_to_outcome_seconds) || 0,
        },
      ];
      return acc;
    }, {});
    overrideRecommendationsByRuleIndex.value = (
      recommendationResponse?.data?.data || []
    ).reduce((acc, row) => {
      const index = Number(row?.key);
      if (!Number.isFinite(index)) return acc;
      acc[index] = {
        title: String(row?.title || '').trim(),
        body: String(row?.body || '').trim(),
        evidenceSummary: String(row?.evidence_summary || '').trim(),
        suggestedChange: String(row?.suggested_change || '').trim(),
        nextStep: String(row?.next_step || '').trim(),
        severity: String(row?.severity || '').trim(),
        recommendationCode: String(row?.recommendation_code || '').trim(),
      };
      return acc;
    }, {});
  } catch {
    overrideEffectivenessByRuleIndex.value = {};
    overrideInboxBreakdownByRuleIndex.value = {};
    overrideConditionRollupsByRuleIndex.value = {};
    overrideGuardRollupsByRuleIndex.value = {};
    overrideConditionRecommendationsByRuleIndex.value = {};
    overrideGuardRecommendationsByRuleIndex.value = {};
    overrideRecentCasesByRuleIndex.value = {};
    overrideRecommendationsByRuleIndex.value = {};
  }
};

const handleSave = async () => {
  if (!props.assistant?.id) return;

  isSubmitting.value = true;
  try {
    const existingPromptSections = aiCloserConfig.value?.prompt_sections || {};
    const existingTemplateMode = existingPromptSections.template_mode || {};
    const existingPlaybooks = existingPromptSections.playbooks || {};
    const existingProcedureRouting = existingPromptSections.procedure_routing || {};
    const existingRiskPolicy = existingPromptSections.risk_policy || {};

    const nextActionByTier = {
      ...(existingRiskPolicy.action_by_tier || { L0: 'inherit', L1: 'inherit', L2: 'auto_suggest' }),
      L3: highRiskAction.value,
    };
    const nextAttributeOverrides = buildAttributeOverridePayload();

    await store.dispatch('aiCloser/updateConfig', {
      assistantId: props.assistant.id,
      data: {
        prompt_sections: {
          ...existingPromptSections,
          playbooks: {
            ...existingPlaybooks,
            enabled: procedureRoutingEnabled.value,
          },
          procedure_routing: {
            ...existingProcedureRouting,
            enabled: procedureRoutingEnabled.value,
            routing_precedence: procedureRoutingPrecedence.value,
            step_confirmation_strategy: procedureStepConfirmationStrategy.value,
            fallback_action: procedureFallbackAction.value,
          },
          template_mode: {
            ...existingTemplateMode,
            enabled: templateModeEnabled.value,
            on_no_match: uncertainAction.value,
            ask_clarify_th: askClarifyTh.value,
            ask_clarify_en: askClarifyEn.value,
            default_reply_th: defaultReplyTh.value,
            default_reply_en: defaultReplyEn.value,
          },
          risk_policy: {
            ...existingRiskPolicy,
            enabled: riskEnabled.value,
            action_by_tier: nextActionByTier,
            attribute_overrides: nextAttributeOverrides,
          },
        },
      },
    });

    alert('Escalation settings saved to AI runtime config');
    fetchRecentEscalations();
    fetchOverrideEffectiveness();
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to save escalation settings'
    );
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => aiCloserConfig.value,
  value => {
    if (props.isSavingAll) return;
    syncFromConfig(value);
  },
  { immediate: true, deep: true }
);

watch(
  () => [
    route.query?.escalation_seed,
    route.query?.escalation_seed_source,
    route.query?.escalation_seed_focus,
    route.query?.escalation_seed_reason,
    route.query?.escalation_seed_query,
    route.query?.escalation_seed_label,
  ],
  () => {
    if (!escalationSeedContext.value.enabled) return;
    focusSeededEscalationTarget();
  },
  { immediate: true }
);

onMounted(() => {
  fetchRuntimeConfig();
  if (!automations.value.length) {
    store.dispatch('automations/get');
  }
  if (props.assistant?.id) {
    store.dispatch('ailabInboxes/get', {
      assistantId: props.assistant.id,
    });
    store.dispatch('inboxes/get');
  }
  fetchRecentEscalations();
  fetchOverrideEffectiveness();
  if (escalationSeedContext.value.enabled) {
    focusSeededEscalationTarget();
  }
});

defineExpose({
  save: handleSave,
});
</script>

<template>
  <div class="fin-ai flex flex-col h-full min-h-0">
    <div
      class="flex flex-col h-full min-h-0 overflow-hidden rounded-module bg-base-module shadow-lvl-0"
    >
      <div class="border-b border-neutral-border">
        <div class="mb-1">
          <div class="fin-page-header-row">
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="btn o__secondary o__icon o__subtle"
                aria-label="Toggle sidebar"
              >
                <span class="i-lucide-panel-left-open size-4" />
              </button>
              <h1 class="heading o__size-1">Escalation</h1>
            </div>

            <div class="flex items-center space-x-2">
              <div class="escalation-owner-badge">Runtime owner</div>
              <button
                type="button"
                class="btn o__secondary"
                :disabled="isSubmitting || isLoadingConfig"
                @click="handleSave"
              >
                {{ isSubmitting ? 'Saving...' : 'Save escalation' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="fin-page-body">
        <div class="escalation-intro-card fin-page-section">
          <div class="fin-page-intro-title">Escalation now owns runtime safety routing</div>
          <div class="fin-page-intro-copy mt-1">
            This page writes
            <code class="escalation-inline-code">prompt_sections.risk_policy</code>
            and the uncertainty fallback fields in
            <code class="escalation-inline-code">prompt_sections.template_mode</code>.
          </div>
        </div>

        <div
          v-if="escalationSeedContext.enabled"
          class="escalation-intro-card fin-page-section"
        >
          <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div class="fin-page-intro-title">Escalation seeded context</div>
              <div class="fin-page-intro-copy mt-1">
                {{ escalationSeedSummary || 'A routing context was brought into Escalation.' }}
              </div>
              <div
                v-if="escalationSeedContext.query"
                class="fin-page-intro-copy mt-1"
              >
                Sample query: {{ escalationSeedContext.query }}
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <button type="button" class="btn o__secondary" @click="focusSeededEscalationTarget">
                {{ escalationSeedPrimaryActionLabel }}
              </button>
              <button
                v-if="escalationSeedContext.query"
                type="button"
                class="btn o__secondary"
                @click="retestSeededEscalationContext"
              >
                Retest sample
              </button>
              <button type="button" class="btn o__secondary" @click="clearEscalationSeed">
                Clear seeded context
              </button>
            </div>
          </div>
        </div>

        <div v-if="isLoadingConfig" class="escalation-loading-state">
          <span class="i-lucide-loader-2 size-5 animate-spin" />
          <span>Loading escalation settings...</span>
        </div>

        <template v-else>
          <div ref="procedureRoutingSectionRef" class="fin-page-section">
            <h3 class="text-h3 font-semibold mb-2">Procedure and tool routing</h3>
            <p class="text-sm fin-muted">
              Procedures themselves live in the Procedures workspace. Escalation owns
              what runtime should do when that routing is uncertain or when risk
              policy needs to take over.
            </p>

            <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                v-for="card in procedureRoutingCards"
                :key="card.label"
                class="border border-neutral-border rounded-large p-4"
              >
                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                  {{ card.label }}
                </div>
                <div class="text-2xl font-semibold mt-2">{{ card.value }}</div>
                <div class="text-sm fin-muted mt-2">{{ card.note }}</div>
              </div>
            </div>

            <div class="escalation-intro-card mt-4">
              <div class="fin-page-intro-title">
                Routing owner snapshot
              </div>
              <div class="fin-page-intro-copy mt-1">
                <code class="escalation-inline-code">prompt_sections.slot_schema</code>
                is still the real owner for procedures. This page now writes the
                wrapper defaults in
                <code class="escalation-inline-code">prompt_sections.procedure_routing</code>
                plus the fallback and risk routing that wrap around those procedures
                at runtime.
              </div>
              <div class="flex flex-wrap gap-2 mt-3">
                <button type="button" class="btn o__secondary" @click="openProceduresTab">
                  Open Procedures
                </button>
                <button type="button" class="btn o__secondary" @click="openAnalyzeTab">
                  Open Analyze
                </button>
              </div>
            </div>

            <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="border border-neutral-border rounded-large p-4">
                <div class="font-semibold">Procedure routing defaults</div>
                <div class="text-sm fin-muted mt-1">
                  Use these defaults when a procedure exists, but the procedure flow
                  itself does not set a more specific routing rule.
                </div>
                <label class="inline-flex items-center gap-2 cursor-pointer mt-3">
                  <input
                    v-model="procedureRoutingEnabled"
                    type="checkbox"
                    class="h-4 w-4"
                  />
                  <span class="text-sm text-slate-900">
                    Let procedures handle matched cases before the general AI path
                  </span>
                </label>
                <label class="block mt-4">
                  <span class="text-sm font-medium text-slate-900">
                    Template miss precedence
                  </span>
                  <select
                    v-model="procedureRoutingPrecedence"
                    class="escalation-select escalation-select--offset mt-2"
                    :disabled="!procedureRoutingEnabled"
                  >
                    <option value="procedure_before_fallback">
                      Try a procedure before the fallback route
                    </option>
                    <option value="fallback_before_procedure">
                      Use the fallback route immediately
                    </option>
                  </select>
                </label>
              </div>

              <div class="border border-neutral-border rounded-large p-4">
                <div class="font-semibold">Procedure recovery behavior</div>
                <div class="text-sm fin-muted mt-1">
                  These defaults only apply when the procedure schema does not define
                  its own per-intent question cadence or fallback action.
                </div>
                <label class="block mt-4">
                  <span class="text-sm font-medium text-slate-900">
                    Step confirmation style
                  </span>
                  <select
                    v-model="procedureStepConfirmationStrategy"
                    class="escalation-select escalation-select--offset mt-2"
                    :disabled="!procedureRoutingEnabled"
                  >
                    <option value="confirm_each_step">Ask for one detail at a time</option>
                    <option value="balanced">Ask for a small balanced set</option>
                    <option value="collect_brief_batch">Collect a short batch before confirming</option>
                  </select>
                </label>
                <label class="block mt-4">
                  <span class="text-sm font-medium text-slate-900">
                    Fallback on incomplete procedure
                  </span>
                  <select
                    v-model="procedureFallbackAction"
                    class="escalation-select escalation-select--offset mt-2"
                    :disabled="!procedureRoutingEnabled"
                  >
                    <option value="ask_clarify">Ask one more clarifying question</option>
                    <option value="default_reply">Send a neutral holding reply</option>
                    <option value="handoff">Handoff to a human</option>
                  </select>
                </label>
              </div>
            </div>
          </div>

          <div ref="defaultBehaviorSectionRef" class="fin-page-section">
            <h3 class="text-h3 font-semibold mb-2">Default behavior</h3>
            <p class="text-sm fin-muted">
              Decide what the AI should do when it is unsure or when a high-risk
              situation is detected.
            </p>

            <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="border border-neutral-border rounded-large p-4">
                <div class="font-semibold">When uncertain</div>
                <div class="text-sm fin-muted mt-1">
                  This maps to runtime template-mode fallback behavior and copy.
                </div>
                <label class="inline-flex items-center gap-2 cursor-pointer mt-3">
                  <input
                    v-model="templateModeEnabled"
                    type="checkbox"
                    class="h-4 w-4"
                  />
                  <span class="text-sm text-slate-900">
                    Enable template-mode fallback routing
                  </span>
                </label>
                <select
                  v-model="uncertainAction"
                  class="escalation-select escalation-select--offset mt-3"
                  :disabled="!templateModeEnabled"
                >
                  <option value="ask_clarify">Ask a clarifying question</option>
                  <option value="fallback_ai">Continue with AI</option>
                  <option value="default_reply">Send default reply</option>
                </select>

                <div
                  v-if="templateModeEnabled && uncertainAction === 'ask_clarify'"
                  class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3"
                >
                  <textarea
                    v-model="askClarifyTh"
                    class="escalation-textarea"
                    rows="3"
                    placeholder="Thai ask-clarify fallback"
                  />
                  <textarea
                    v-model="askClarifyEn"
                    class="escalation-textarea"
                    rows="3"
                    placeholder="English ask-clarify fallback"
                  />
                </div>

                <div
                  v-if="templateModeEnabled && uncertainAction === 'default_reply'"
                  class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3"
                >
                  <textarea
                    v-model="defaultReplyTh"
                    class="escalation-textarea"
                    rows="3"
                    placeholder="Thai default reply fallback"
                  />
                  <textarea
                    v-model="defaultReplyEn"
                    class="escalation-textarea"
                    rows="3"
                    placeholder="English default reply fallback"
                  />
                </div>
              </div>

              <div class="border border-neutral-border rounded-large p-4">
                <div class="font-semibold">When risky</div>
                <div class="text-sm fin-muted mt-1">
                  This controls the default L3 tier action in risk policy.
                </div>
                <select
                  v-model="highRiskAction"
                  class="escalation-select escalation-select--offset mt-3"
                  :disabled="!riskEnabled"
                >
                  <option value="inherit">Inherit current mode</option>
                  <option value="auto_suggest">Auto suggest</option>
                  <option value="suggest">Suggest only</option>
                  <option value="handoff">Handoff</option>
                </select>
              </div>
            </div>
          </div>

          <div ref="escalationRulesSectionRef" class="fin-page-section">
            <h3 class="text-h3 font-semibold mb-2">Escalation rules</h3>
            <p class="text-sm fin-muted">
              Deterministic conditions that trigger stricter handling.
            </p>

            <div class="mt-4 border border-neutral-border rounded-large p-4">
              <label class="inline-flex items-center gap-2 cursor-pointer">
                <input
                  v-model="riskEnabled"
                  type="checkbox"
                  class="h-4 w-4"
                />
                <span class="text-sm text-slate-900">Risk policy enabled</span>
              </label>

              <div class="mt-5 flex items-center justify-between gap-3">
                <div>
                  <div class="font-semibold">
                    {{ automations.length }} automation rules available
                  </div>
                  <div class="text-sm fin-muted mt-1">
                    Use automation rules for inbox-level or non-AI escalation
                    routing.
                  </div>
                </div>
                <button
                  type="button"
                  class="btn o__secondary"
                  @click="openAutomationRules"
                >
                  <span class="i-lucide-settings size-4" />
                  Manage rules
                </button>
              </div>

              <div class="mt-5 border border-neutral-border rounded-large p-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="font-semibold">Attribute-based escalation overrides</div>
                    <div class="text-sm fin-muted mt-1">
                      Match detected AI attributes and force a stricter risk tier or escalation action before the normal intent policy path wins.
                    </div>
                  </div>
                  <button
                    type="button"
                    class="btn o__secondary"
                    :disabled="!riskEnabled"
                    @click="addAttributeOverride"
                  >
                    Add override
                  </button>
                </div>

                <div v-if="attributeOverrides.length" class="mt-4 escalation-override-list">
                  <div
                    v-for="(override, index) in attributeOverrides"
                    :key="override.id"
                    class="escalation-override-card"
                  >
                    <div class="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div class="font-semibold">
                          {{ override.label || `Rule ${index + 1}` }}
                        </div>
                        <div class="text-sm fin-muted mt-1">
                          {{ overrideDraftSummary(override) || 'No conditions yet' }}
                        </div>
                      </div>
                      <div class="text-right">
                        <div class="text-xs fin-muted">
                          Outcomes in last {{ ATTRIBUTE_OVERRIDE_EFFECTIVENESS_DAYS }}d
                        </div>
                        <div class="text-lg font-semibold">
                          {{ overrideRecentHitCount(index) }}
                        </div>
                        <div class="text-xs fin-muted mt-1">
                          resolved {{ overrideResolvedCount(index) }} • escalated
                          {{ overrideEscalatedCount(index) }} • pending
                          {{ overrideUnresolvedCount(index) }}
                        </div>
                        <div class="text-xs fin-muted mt-1">
                          {{ Math.round(overrideResolvedRate(index) * 100) }}% resolved /
                          {{ Math.round(overrideEscalatedRate(index) * 100) }}% escalated
                        </div>
                        <div
                          v-if="overrideTopInboxLabel(index)"
                          class="text-xs fin-muted mt-1"
                        >
                          Top inbox {{ overrideTopInboxLabel(index) }}:
                          {{ overrideTopInboxUsedCount(index) }} used • resolved
                          {{ overrideTopInboxResolvedCount(index) }} • escalated
                          {{ overrideTopInboxEscalatedCount(index) }}
                        </div>
                        <div
                          v-if="overrideInboxBreakdownRows(index).length"
                          class="text-xs fin-muted mt-1"
                        >
                          Hotspots:
                          {{
                            overrideInboxBreakdownRows(index)
                              .slice(0, 3)
                              .map(formatOverrideInboxBreakdownRow)
                              .join(' • ')
                          }}
                        </div>
                        <div
                          v-if="overrideRecentCaseRows(index).length"
                          class="text-xs fin-muted mt-1"
                        >
                          Recent cases:
                          {{
                            overrideRecentCaseRows(index)
                              .slice(0, 3)
                              .map(formatOverrideRecentCaseRow)
                              .join(' • ')
                          }}
                        </div>
                        <div
                          v-if="recentOverrideMatchedConditionSummaries(index).length"
                          class="text-xs fin-muted mt-1"
                        >
                          Matched conditions:
                          {{
                            formatOverrideRecentSummaryList(
                              recentOverrideMatchedConditionSummaries(index)
                            )
                          }}
                        </div>
                        <div
                          v-if="recentOverrideMatchedConditionSummaries(index).length"
                          class="mt-1 flex flex-wrap justify-end gap-2"
                        >
                          <button
                            v-for="condition in recentOverrideMatchedConditionSummaries(index)"
                            :key="`override-condition-${index}-${condition}`"
                            type="button"
                            class="btn o__secondary"
                            @click="inspectOverrideConditionInAnalyze(index, override, condition)"
                          >
                            {{ condition }}
                          </button>
                        </div>
                        <div
                          v-if="topOverrideConditionRollupRows(index).length"
                          class="text-xs fin-muted mt-1"
                        >
                          Condition hotspots:
                          {{
                            topOverrideConditionRollupRows(index)
                              .map(formatOverrideBranchRollupRow)
                              .join(' • ')
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRollupRows(index).length"
                          class="text-xs fin-muted mt-1"
                        >
                          Condition evidence:
                          {{
                            topOverrideConditionRollupRows(index)
                              .map(
                                row =>
                                  `${formatOverrideBranchTopInbox(row)}${
                                    formatOverrideBranchLatestCase(row)
                                      ? ` • latest ${formatOverrideBranchLatestCase(row)}`
                                      : ''
                                  }${
                                    formatOverrideBranchComparedCase(row, 'resolved')
                                      ? ` • resolved ${formatOverrideBranchComparedCase(row, 'resolved')}`
                                      : ''
                                  }${
                                    formatOverrideBranchComparedCase(row, 'escalated')
                                      ? ` • escalated ${formatOverrideBranchComparedCase(row, 'escalated')}`
                                      : ''
                                  }`
                              )
                              .filter(Boolean)
                              .join(' • ')
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)"
                          class="text-xs mt-1"
                        >
                          Condition recommendation:
                          {{ topOverrideConditionRecommendation(index).title }}.
                          {{
                            topOverrideConditionRecommendation(index)
                              .suggestedChange ||
                              topOverrideConditionRecommendation(index).body
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.evidenceSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          Why:
                          {{
                            topOverrideConditionRecommendation(index).evidenceSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.fixIntentSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          Fix intent:
                          {{
                            topOverrideConditionRecommendation(index).fixIntentSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.fixBriefTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Brief:
                          {{ topOverrideConditionRecommendation(index).fixBriefTitle }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.fixBriefBody"
                          class="text-xs fin-muted mt-1"
                        >
                          {{ topOverrideConditionRecommendation(index).fixBriefBody }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.fixBriefValidation"
                          class="text-xs fin-muted mt-1"
                        >
                          Validate:
                          {{
                            topOverrideConditionRecommendation(index).fixBriefValidation
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Bundle:
                          {{
                            topOverrideConditionRecommendation(index).repairBundleTitle
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleResolvedSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleResolvedSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleEscalatedSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleEscalatedSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleValidationSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          Validate slice:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleValidationSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Workflow:
                          {{
                            [
                              topOverrideConditionRecommendation(index)
                                .repairBundleHotspotStageTitle,
                              topOverrideConditionRecommendation(index)
                                .repairBundleResolvedStageTitle,
                              topOverrideConditionRecommendation(index)
                                .repairBundleEscalatedStageTitle,
                              topOverrideConditionRecommendation(index)
                                .repairBundleValidationStageTitle,
                            ]
                              .filter(Boolean)
                              .join(' -> ')
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageEvidence"
                          class="text-xs fin-muted mt-1"
                        >
                          First evidence:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageEvidence
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageFocus"
                          class="text-xs fin-muted mt-1"
                        >
                          First check:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageFocus
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageDecision"
                          class="text-xs fin-muted mt-1"
                        >
                          First decision:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageDecision
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageRepairMove"
                          class="text-xs fin-muted mt-1"
                        >
                          First repair:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageRepairMove
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageExecutionBrief"
                          class="text-xs fin-muted mt-1"
                        >
                          First execution:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageExecutionBrief
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageWatchpoint"
                          class="text-xs fin-muted mt-1"
                        >
                          First watch:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageWatchpoint
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageValidationFocus"
                          class="text-xs fin-muted mt-1"
                        >
                          First validation focus:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageValidationFocus
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageOwnerPacketTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          First owner packet:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageOwnerPacketTitle
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageOwnerPacketBody"
                          class="text-xs fin-muted mt-1"
                        >
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageOwnerPacketBody
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageOwnerRole"
                          class="text-xs fin-muted mt-1"
                        >
                          First owner role:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageOwnerRole
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageHandoffTarget"
                          class="text-xs fin-muted mt-1"
                        >
                          First handoff target:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageHandoffTarget
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageSuccessCheck"
                          class="text-xs fin-muted mt-1"
                        >
                          First success check:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageSuccessCheck
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageLedgerStatus"
                          class="text-xs fin-muted mt-1"
                        >
                          First ledger status:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageLedgerStatus
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageLastAction"
                          class="text-xs fin-muted mt-1"
                        >
                          First last action:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageLastAction
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageReopenRule"
                          class="text-xs fin-muted mt-1"
                        >
                          First reopen rule:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageReopenRule
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageNextOwner"
                          class="text-xs fin-muted mt-1"
                        >
                          First next owner:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageNextOwner
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageBlockedReason"
                          class="text-xs fin-muted mt-1"
                        >
                          First blocked reason:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageBlockedReason
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageCompletionGate"
                          class="text-xs fin-muted mt-1"
                        >
                          First completion gate:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageCompletionGate
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageDoneState"
                          class="text-xs fin-muted mt-1"
                        >
                          First done state:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageDoneState
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageFailState"
                          class="text-xs fin-muted mt-1"
                        >
                          First fail state:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageFailState
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotStageRollbackTrigger"
                          class="text-xs fin-muted mt-1"
                        >
                          First rollback trigger:
                          {{
                            topOverrideConditionRecommendation(index)
                              .repairBundleHotspotStageRollbackTrigger
                          }}
                        </div>
                        <div class="flex flex-wrap gap-2 mt-2">
                          <button
                            v-if="topOverrideConditionRecommendation(index)?.repairBundleHotspotActionTitle"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideConditionRepairBundleActionInAnalyze(
                                index,
                                override,
                                topOverrideConditionRecommendation(index),
                                'hotspot'
                              )
                            "
                          >
                            {{
                              topOverrideConditionRecommendation(index)
                                .repairBundleHotspotActionTitle
                            }}
                          </button>
                          <button
                            v-if="topOverrideConditionRecommendation(index)?.repairBundleResolvedActionTitle"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideConditionRepairBundleActionInAnalyze(
                                index,
                                override,
                                topOverrideConditionRecommendation(index),
                                'resolved'
                              )
                            "
                          >
                            {{
                              topOverrideConditionRecommendation(index)
                                .repairBundleResolvedActionTitle
                            }}
                          </button>
                          <button
                            v-if="topOverrideConditionRecommendation(index)?.repairBundleEscalatedActionTitle"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideConditionRepairBundleActionInAnalyze(
                                index,
                                override,
                                topOverrideConditionRecommendation(index),
                                'escalated'
                              )
                            "
                          >
                            {{
                              topOverrideConditionRecommendation(index)
                                .repairBundleEscalatedActionTitle
                            }}
                          </button>
                          <button
                            v-if="topOverrideConditionRecommendation(index)?.repairBundleValidationActionTitle"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideConditionRepairBundleActionInAnalyze(
                                index,
                                override,
                                topOverrideConditionRecommendation(index),
                                'validation'
                              )
                            "
                          >
                            {{
                              topOverrideConditionRecommendation(index)
                                .repairBundleValidationActionTitle
                            }}
                          </button>
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.fixPresetTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Preset:
                          {{ topOverrideConditionRecommendation(index).fixPresetTitle }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.fixPresetBody"
                          class="text-xs fin-muted mt-1"
                        >
                          Pattern:
                          {{ topOverrideConditionRecommendation(index).fixPresetBody }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.compareFocusTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Diagnosis:
                          {{
                            topOverrideConditionRecommendation(index).compareFocusTitle
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.compareSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          Compare:
                          {{
                            topOverrideConditionRecommendation(index).compareSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.compareFocus"
                          class="text-xs fin-muted mt-1"
                        >
                          Focus:
                          {{
                            topOverrideConditionRecommendation(index).compareFocus
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.compareNextStep"
                          class="text-xs fin-muted mt-1"
                        >
                          Compare next:
                          {{
                            topOverrideConditionRecommendation(index).compareNextStep
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRecommendation(index)?.nextStep"
                          class="text-xs fin-muted mt-1"
                        >
                          Next:
                          {{ topOverrideConditionRecommendation(index).nextStep }}
                        </div>
                        <div
                          v-if="
                            formatOverrideBranchComparedCase(
                              topOverrideConditionRecommendation(index),
                              'resolved'
                            )
                          "
                          class="text-xs fin-muted mt-1"
                        >
                          Compare resolved:
                          {{
                            formatOverrideBranchComparedCase(
                              topOverrideConditionRecommendation(index),
                              'resolved'
                            )
                          }}
                        </div>
                        <div
                          v-if="
                            formatOverrideBranchComparedCase(
                              topOverrideConditionRecommendation(index),
                              'escalated'
                            )
                          "
                          class="text-xs fin-muted mt-1"
                        >
                          Compare escalated:
                          {{
                            formatOverrideBranchComparedCase(
                              topOverrideConditionRecommendation(index),
                              'escalated'
                            )
                          }}
                        </div>
                        <div
                          v-if="topOverrideConditionRollupRows(index).length"
                          class="mt-1 flex flex-wrap justify-end gap-2"
                        >
                          <template
                            v-for="row in topOverrideConditionRollupRows(index)"
                            :key="`override-condition-rollup-${index}-${row.key}`"
                          >
                            <button
                              type="button"
                              class="btn o__secondary"
                              @click="inspectOverrideConditionInAnalyze(index, override, row.label)"
                            >
                              {{ row.label }}
                            </button>
                            <button
                              v-if="row.topInboxId"
                              type="button"
                              class="btn o__secondary"
                              @click="inspectOverrideConditionTopInboxInAnalyze(index, override, row)"
                            >
                              Top inbox
                            </button>
                            <button
                              v-if="row.latestConversationId"
                              type="button"
                              class="btn o__secondary"
                              @click="inspectOverrideConditionLatestCaseInAnalyze(index, override, row)"
                            >
                              Latest case
                            </button>
                            <button
                              v-if="row.latestResolvedConversationId"
                              type="button"
                              class="btn o__secondary"
                              @click="
                                inspectOverrideConditionResolvedCaseInAnalyze(
                                  index,
                                  override,
                                  row
                                )
                              "
                            >
                              Resolved case
                            </button>
                            <button
                              v-if="row.latestEscalatedConversationId"
                              type="button"
                              class="btn o__secondary"
                              @click="
                                inspectOverrideConditionEscalatedCaseInAnalyze(
                                  index,
                                  override,
                                  row
                                )
                              "
                            >
                              Escalated case
                            </button>
                          </template>
                          <button
                            v-if="topOverrideConditionRecommendation(index)?.compareSummary"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideConditionCompareInAnalyze(
                                index,
                                override,
                                topOverrideConditionRecommendation(index)
                              )
                            "
                          >
                            Compare branch
                          </button>
                          <button
                            v-if="topOverrideConditionRecommendation(index)"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideConditionRecommendationInAnalyze(
                                index,
                                override,
                                topOverrideConditionRecommendation(index)
                              )
                            "
                          >
                            Fix branch
                          </button>
                        </div>
                        <div
                          v-if="recentOverridePassedGuardSummaries(index).length"
                          class="text-xs fin-muted mt-1"
                        >
                          Passed guards:
                          {{
                            formatOverrideRecentSummaryList(
                              recentOverridePassedGuardSummaries(index)
                            )
                          }}
                        </div>
                        <div
                          v-if="recentOverridePassedGuardSummaries(index).length"
                          class="mt-1 flex flex-wrap justify-end gap-2"
                        >
                          <button
                            v-for="guard in recentOverridePassedGuardSummaries(index)"
                            :key="`override-guard-${index}-${guard}`"
                            type="button"
                            class="btn o__secondary"
                            @click="inspectOverrideGuardInAnalyze(index, override, guard)"
                          >
                            {{ guard }}
                          </button>
                        </div>
                        <div
                          v-if="topOverrideGuardRollupRows(index).length"
                          class="text-xs fin-muted mt-1"
                        >
                          Guard hotspots:
                          {{
                            topOverrideGuardRollupRows(index)
                              .map(formatOverrideBranchRollupRow)
                              .join(' • ')
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRollupRows(index).length"
                          class="text-xs fin-muted mt-1"
                        >
                          Guard evidence:
                          {{
                            topOverrideGuardRollupRows(index)
                              .map(
                                row =>
                                  `${formatOverrideBranchTopInbox(row)}${
                                    formatOverrideBranchLatestCase(row)
                                      ? ` • latest ${formatOverrideBranchLatestCase(row)}`
                                      : ''
                                  }${
                                    formatOverrideBranchComparedCase(row, 'resolved')
                                      ? ` • resolved ${formatOverrideBranchComparedCase(row, 'resolved')}`
                                      : ''
                                  }${
                                    formatOverrideBranchComparedCase(row, 'escalated')
                                      ? ` • escalated ${formatOverrideBranchComparedCase(row, 'escalated')}`
                                      : ''
                                  }`
                              )
                              .filter(Boolean)
                              .join(' • ')
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)"
                          class="text-xs mt-1"
                        >
                          Guard recommendation:
                          {{ topOverrideGuardRecommendation(index).title }}.
                          {{
                            topOverrideGuardRecommendation(index)
                              .suggestedChange ||
                              topOverrideGuardRecommendation(index).body
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.evidenceSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          Why:
                          {{ topOverrideGuardRecommendation(index).evidenceSummary }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.fixIntentSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          Fix intent:
                          {{ topOverrideGuardRecommendation(index).fixIntentSummary }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.fixBriefTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Brief:
                          {{ topOverrideGuardRecommendation(index).fixBriefTitle }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.fixBriefBody"
                          class="text-xs fin-muted mt-1"
                        >
                          {{ topOverrideGuardRecommendation(index).fixBriefBody }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.fixBriefValidation"
                          class="text-xs fin-muted mt-1"
                        >
                          Validate:
                          {{ topOverrideGuardRecommendation(index).fixBriefValidation }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Bundle:
                          {{ topOverrideGuardRecommendation(index).repairBundleTitle }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleResolvedSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleResolvedSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleEscalatedSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleEscalatedSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleValidationSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          Validate slice:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleValidationSummary
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Workflow:
                          {{
                            [
                              topOverrideGuardRecommendation(index)
                                .repairBundleHotspotStageTitle,
                              topOverrideGuardRecommendation(index)
                                .repairBundleResolvedStageTitle,
                              topOverrideGuardRecommendation(index)
                                .repairBundleEscalatedStageTitle,
                              topOverrideGuardRecommendation(index)
                                .repairBundleValidationStageTitle,
                            ]
                              .filter(Boolean)
                              .join(' -> ')
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageEvidence"
                          class="text-xs fin-muted mt-1"
                        >
                          First evidence:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageEvidence
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageFocus"
                          class="text-xs fin-muted mt-1"
                        >
                          First check:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageFocus
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageDecision"
                          class="text-xs fin-muted mt-1"
                        >
                          First decision:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageDecision
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageRepairMove"
                          class="text-xs fin-muted mt-1"
                        >
                          First repair:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageRepairMove
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageExecutionBrief"
                          class="text-xs fin-muted mt-1"
                        >
                          First execution:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageExecutionBrief
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageWatchpoint"
                          class="text-xs fin-muted mt-1"
                        >
                          First watch:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageWatchpoint
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageValidationFocus"
                          class="text-xs fin-muted mt-1"
                        >
                          First validation focus:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageValidationFocus
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageOwnerPacketTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          First owner packet:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageOwnerPacketTitle
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageOwnerPacketBody"
                          class="text-xs fin-muted mt-1"
                        >
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageOwnerPacketBody
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageOwnerRole"
                          class="text-xs fin-muted mt-1"
                        >
                          First owner role:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageOwnerRole
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageHandoffTarget"
                          class="text-xs fin-muted mt-1"
                        >
                          First handoff target:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageHandoffTarget
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageSuccessCheck"
                          class="text-xs fin-muted mt-1"
                        >
                          First success check:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageSuccessCheck
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageLedgerStatus"
                          class="text-xs fin-muted mt-1"
                        >
                          First ledger status:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageLedgerStatus
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageLastAction"
                          class="text-xs fin-muted mt-1"
                        >
                          First last action:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageLastAction
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageReopenRule"
                          class="text-xs fin-muted mt-1"
                        >
                          First reopen rule:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageReopenRule
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageNextOwner"
                          class="text-xs fin-muted mt-1"
                        >
                          First next owner:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageNextOwner
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageBlockedReason"
                          class="text-xs fin-muted mt-1"
                        >
                          First blocked reason:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageBlockedReason
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageCompletionGate"
                          class="text-xs fin-muted mt-1"
                        >
                          First completion gate:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageCompletionGate
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageDoneState"
                          class="text-xs fin-muted mt-1"
                        >
                          First done state:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageDoneState
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageFailState"
                          class="text-xs fin-muted mt-1"
                        >
                          First fail state:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageFailState
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotStageRollbackTrigger"
                          class="text-xs fin-muted mt-1"
                        >
                          First rollback trigger:
                          {{
                            topOverrideGuardRecommendation(index)
                              .repairBundleHotspotStageRollbackTrigger
                          }}
                        </div>
                        <div class="flex flex-wrap gap-2 mt-2">
                          <button
                            v-if="topOverrideGuardRecommendation(index)?.repairBundleHotspotActionTitle"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideGuardRepairBundleActionInAnalyze(
                                index,
                                override,
                                topOverrideGuardRecommendation(index),
                                'hotspot'
                              )
                            "
                          >
                            {{
                              topOverrideGuardRecommendation(index)
                                .repairBundleHotspotActionTitle
                            }}
                          </button>
                          <button
                            v-if="topOverrideGuardRecommendation(index)?.repairBundleResolvedActionTitle"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideGuardRepairBundleActionInAnalyze(
                                index,
                                override,
                                topOverrideGuardRecommendation(index),
                                'resolved'
                              )
                            "
                          >
                            {{
                              topOverrideGuardRecommendation(index)
                                .repairBundleResolvedActionTitle
                            }}
                          </button>
                          <button
                            v-if="topOverrideGuardRecommendation(index)?.repairBundleEscalatedActionTitle"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideGuardRepairBundleActionInAnalyze(
                                index,
                                override,
                                topOverrideGuardRecommendation(index),
                                'escalated'
                              )
                            "
                          >
                            {{
                              topOverrideGuardRecommendation(index)
                                .repairBundleEscalatedActionTitle
                            }}
                          </button>
                          <button
                            v-if="topOverrideGuardRecommendation(index)?.repairBundleValidationActionTitle"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideGuardRepairBundleActionInAnalyze(
                                index,
                                override,
                                topOverrideGuardRecommendation(index),
                                'validation'
                              )
                            "
                          >
                            {{
                              topOverrideGuardRecommendation(index)
                                .repairBundleValidationActionTitle
                            }}
                          </button>
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.fixPresetTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Preset:
                          {{ topOverrideGuardRecommendation(index).fixPresetTitle }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.fixPresetBody"
                          class="text-xs fin-muted mt-1"
                        >
                          Pattern:
                          {{ topOverrideGuardRecommendation(index).fixPresetBody }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.compareFocusTitle"
                          class="text-xs fin-muted mt-1"
                        >
                          Diagnosis:
                          {{ topOverrideGuardRecommendation(index).compareFocusTitle }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.compareSummary"
                          class="text-xs fin-muted mt-1"
                        >
                          Compare:
                          {{ topOverrideGuardRecommendation(index).compareSummary }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.compareFocus"
                          class="text-xs fin-muted mt-1"
                        >
                          Focus:
                          {{ topOverrideGuardRecommendation(index).compareFocus }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.compareNextStep"
                          class="text-xs fin-muted mt-1"
                        >
                          Compare next:
                          {{ topOverrideGuardRecommendation(index).compareNextStep }}
                        </div>
                        <div
                          v-if="topOverrideGuardRecommendation(index)?.nextStep"
                          class="text-xs fin-muted mt-1"
                        >
                          Next:
                          {{ topOverrideGuardRecommendation(index).nextStep }}
                        </div>
                        <div
                          v-if="
                            formatOverrideBranchComparedCase(
                              topOverrideGuardRecommendation(index),
                              'resolved'
                            )
                          "
                          class="text-xs fin-muted mt-1"
                        >
                          Compare resolved:
                          {{
                            formatOverrideBranchComparedCase(
                              topOverrideGuardRecommendation(index),
                              'resolved'
                            )
                          }}
                        </div>
                        <div
                          v-if="
                            formatOverrideBranchComparedCase(
                              topOverrideGuardRecommendation(index),
                              'escalated'
                            )
                          "
                          class="text-xs fin-muted mt-1"
                        >
                          Compare escalated:
                          {{
                            formatOverrideBranchComparedCase(
                              topOverrideGuardRecommendation(index),
                              'escalated'
                            )
                          }}
                        </div>
                        <div
                          v-if="topOverrideGuardRollupRows(index).length"
                          class="mt-1 flex flex-wrap justify-end gap-2"
                        >
                          <template
                            v-for="row in topOverrideGuardRollupRows(index)"
                            :key="`override-guard-rollup-${index}-${row.key}`"
                          >
                            <button
                              type="button"
                              class="btn o__secondary"
                              @click="inspectOverrideGuardInAnalyze(index, override, row.label)"
                            >
                              {{ row.label }}
                            </button>
                            <button
                              v-if="row.topInboxId"
                              type="button"
                              class="btn o__secondary"
                              @click="inspectOverrideGuardTopInboxInAnalyze(index, override, row)"
                            >
                              Top inbox
                            </button>
                            <button
                              v-if="row.latestConversationId"
                              type="button"
                              class="btn o__secondary"
                              @click="inspectOverrideGuardLatestCaseInAnalyze(index, override, row)"
                            >
                              Latest case
                            </button>
                            <button
                              v-if="row.latestResolvedConversationId"
                              type="button"
                              class="btn o__secondary"
                              @click="
                                inspectOverrideGuardResolvedCaseInAnalyze(
                                  index,
                                  override,
                                  row
                                )
                              "
                            >
                              Resolved case
                            </button>
                            <button
                              v-if="row.latestEscalatedConversationId"
                              type="button"
                              class="btn o__secondary"
                              @click="
                                inspectOverrideGuardEscalatedCaseInAnalyze(
                                  index,
                                  override,
                                  row
                                )
                              "
                            >
                              Escalated case
                            </button>
                          </template>
                          <button
                            v-if="topOverrideGuardRecommendation(index)?.compareSummary"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideGuardCompareInAnalyze(
                                index,
                                override,
                                topOverrideGuardRecommendation(index)
                              )
                            "
                          >
                            Compare branch
                          </button>
                          <button
                            v-if="topOverrideGuardRecommendation(index)"
                            type="button"
                            class="btn o__secondary"
                            @click="
                              inspectOverrideGuardRecommendationInAnalyze(
                                index,
                                override,
                                topOverrideGuardRecommendation(index)
                              )
                            "
                          >
                            Fix branch
                          </button>
                        </div>
                        <div
                          v-if="latestOverrideRecentCaseByOutcome(index, 'resolved')"
                          class="text-xs fin-muted mt-1"
                        >
                          Latest resolved:
                          {{ formatLatestOverrideRecentCaseByOutcome(index, 'resolved') }}
                        </div>
                        <div
                          v-if="latestOverrideRecentCaseByOutcome(index, 'escalated')"
                          class="text-xs fin-muted mt-1"
                        >
                          Latest escalated:
                          {{ formatLatestOverrideRecentCaseByOutcome(index, 'escalated') }}
                        </div>
                        <div
                          v-if="latestOverrideRecentCaseByOutcome(index, 'pending')"
                          class="text-xs fin-muted mt-1"
                        >
                          Latest pending:
                          {{ formatLatestOverrideRecentCaseByOutcome(index, 'pending') }}
                        </div>
                        <div class="mt-2 flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            class="btn o__secondary"
                            @click="inspectOverrideRuleInAnalyze(index, override)"
                          >
                            Inspect in Analyze
                          </button>
                          <button
                            v-if="latestOverrideRecentCase(index)"
                            type="button"
                            class="btn o__secondary"
                            @click="inspectLatestOverrideCaseInAnalyze(index, override)"
                          >
                            Open latest case
                          </button>
                        </div>
                        <div class="mt-2 flex flex-wrap justify-end gap-2">
                          <button
                            v-if="overrideEscalatedCount(index) > 0"
                            type="button"
                            class="btn o__secondary"
                            @click="inspectOverrideOutcomeInAnalyze(index, override, 'escalated')"
                          >
                            Escalated cases
                          </button>
                          <button
                            v-if="overrideUnresolvedCount(index) > 0"
                            type="button"
                            class="btn o__secondary"
                            @click="inspectOverrideOutcomeInAnalyze(index, override, 'pending')"
                          >
                            Pending cases
                          </button>
                          <button
                            v-if="overrideResolvedCount(index) > 0"
                            type="button"
                            class="btn o__secondary"
                            @click="inspectOverrideOutcomeInAnalyze(index, override, 'resolved')"
                          >
                            Resolved cases
                          </button>
                        </div>
                        <div
                          v-if="overrideRecommendationTitle(index)"
                          class="text-xs mt-2"
                          :class="overrideRecommendationSeverityClass(index)"
                        >
                          Recommendation: {{ overrideRecommendationTitle(index) }}.
                          {{
                            overrideRecommendationSuggestedChange(index) ||
                              overrideRecommendationBody(index)
                          }}
                        </div>
                        <div
                          v-if="overrideRecommendationEvidence(index)"
                          class="text-xs fin-muted mt-1"
                        >
                          Why: {{ overrideRecommendationEvidence(index) }}
                        </div>
                        <div
                          v-if="overrideRecommendationNextStep(index)"
                          class="text-xs fin-muted mt-1"
                        >
                          Next: {{ overrideRecommendationNextStep(index) }}
                        </div>
                      </div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <label class="escalation-override-field">
                        <span class="fin-muted text-xs">Enabled</span>
                        <label class="inline-flex items-center gap-2 mt-2">
                          <input v-model="override.enabled" type="checkbox" class="h-4 w-4" />
                          <span class="text-sm text-slate-900">Rule active</span>
                        </label>
                      </label>
                      <label class="escalation-override-field">
                        <span class="fin-muted text-xs">Label</span>
                        <input
                          v-model="override.label"
                          type="text"
                          class="escalation-textarea"
                          placeholder="High-risk deposit"
                        />
                      </label>
                      <label class="escalation-override-field">
                        <span class="fin-muted text-xs">Risk tier</span>
                        <select
                          v-model="override.risk_tier"
                          class="escalation-select"
                        >
                          <option value="L0">L0</option>
                          <option value="L1">L1</option>
                          <option value="L2">L2</option>
                          <option value="L3">L3</option>
                        </select>
                      </label>
                      <label class="escalation-override-field">
                        <span class="fin-muted text-xs">Action</span>
                        <div class="flex items-center gap-2">
                          <select
                            v-model="override.action"
                            class="escalation-select"
                          >
                            <option value="inherit">Inherit</option>
                            <option value="auto_suggest">Auto suggest</option>
                            <option value="suggest">Suggest only</option>
                            <option value="handoff">Handoff</option>
                          </select>
                        </div>
                      </label>
                      <label class="escalation-override-field">
                        <span class="fin-muted text-xs">Rule</span>
                        <button
                          type="button"
                          class="btn o__secondary mt-2"
                          @click="removeAttributeOverride(override.id)"
                        >
                          Remove
                        </button>
                      </label>
                    </div>

                    <div class="mt-4 space-y-4">
                      <div
                        v-for="group in attributeOverrideConditionGroups"
                        :key="`${override.id}-${group.key}`"
                        class="border border-neutral-border rounded-large p-3"
                      >
                        <div class="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <div class="font-medium">{{ group.label }}</div>
                            <div class="text-sm fin-muted mt-1">
                              {{
                                group.key === 'all'
                                  ? 'Use this for mandatory conditions.'
                                  : group.key === 'any'
                                    ? 'Use this for optional alternatives.'
                                    : 'Use this to explicitly block a match when these conditions appear.'
                              }}
                            </div>
                          </div>
                          <button
                            type="button"
                            class="btn o__secondary"
                            @click="addOverrideCondition(override, group.key)"
                          >
                            Add condition
                          </button>
                        </div>

                        <div
                          v-if="override.condition_groups[group.key]?.length"
                          class="space-y-3"
                        >
                          <div
                            v-for="condition in override.condition_groups[group.key]"
                            :key="condition.id"
                            class="grid grid-cols-1 md:grid-cols-5 gap-3"
                          >
                            <label class="escalation-override-field">
                              <span class="fin-muted text-xs">Attribute key</span>
                              <input
                                v-model="condition.attribute_key"
                                type="text"
                                class="escalation-textarea"
                                placeholder="intent"
                              />
                            </label>
                            <label class="escalation-override-field">
                              <span class="fin-muted text-xs">Matcher</span>
                              <select v-model="condition.matcher" class="escalation-select">
                                <option
                                  v-for="matcherOption in attributeOverrideMatchers"
                                  :key="matcherOption.value"
                                  :value="matcherOption.value"
                                >
                                  {{ matcherOption.label }}
                                </option>
                              </select>
                            </label>
                            <label
                              v-if="condition.matcher === 'boolean'"
                              class="escalation-override-field"
                            >
                              <span class="fin-muted text-xs">
                                {{ overrideMatcherValueLabel(condition.matcher) }}
                              </span>
                              <select v-model="condition.boolean_value" class="escalation-select">
                                <option value="true">true</option>
                                <option value="false">false</option>
                              </select>
                            </label>
                            <label
                              v-else-if="condition.matcher !== 'present'"
                              class="escalation-override-field"
                            >
                              <span class="fin-muted text-xs">
                                {{ overrideMatcherValueLabel(condition.matcher) }}
                              </span>
                              <input
                                v-if="condition.matcher === 'in'"
                                v-model="condition.values_text"
                                type="text"
                                class="escalation-textarea"
                                :placeholder="overrideMatcherPlaceholder(condition.matcher)"
                              />
                              <input
                                v-else
                                v-model="condition.value"
                                type="text"
                                class="escalation-textarea"
                                :placeholder="overrideMatcherPlaceholder(condition.matcher)"
                              />
                            </label>
                            <label v-else class="escalation-override-field">
                              <span class="fin-muted text-xs">Match value</span>
                              <div class="text-sm fin-muted mt-3">
                                No value needed. This condition fires when the attribute exists.
                              </div>
                            </label>
                            <div class="escalation-override-field">
                              <span class="fin-muted text-xs">Preview</span>
                              <div class="text-sm fin-muted mt-3">
                                {{ conditionDraftSummary(condition) }}
                              </div>
                            </div>
                            <div class="escalation-override-field">
                              <span class="fin-muted text-xs">Condition</span>
                              <button
                                type="button"
                                class="btn o__secondary mt-2"
                                @click="removeOverrideCondition(override, group.key, condition.id)"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-sm fin-muted">
                          No conditions in this group yet.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-sm fin-muted mt-4">
                  No attribute escalation overrides yet. Add rules like
                  <code class="escalation-inline-code">intent = deposit -> L3 handoff</code>
                  or
                  <code class="escalation-inline-code">loan_stage contains late -> handoff</code>
                  or combine
                  <code class="escalation-inline-code">all(intent=deposit, risk_tier=L3) + not(vip=true)</code>
                  when you want detected AI attributes to override the default intent-based risk path.
                </div>
              </div>
            </div>
          </div>

          <div ref="escalationGuidanceSectionRef" class="fin-page-section">
            <h3 class="text-h3 font-semibold mb-2">Escalation guidance</h3>
            <p class="text-sm fin-muted">
              Customer-facing handoff wording still has legacy compatibility
              settings. We are not inventing a new storage owner here yet.
            </p>

            <div class="mt-4 border border-neutral-border rounded-large p-4">
              <div class="font-semibold">
                {{
                  isAIGamer
                    ? 'Compatibility-only advanced settings'
                    : 'Legacy templates retired for this assistant'
                }}
              </div>
              <div class="text-sm fin-muted mt-1">
                {{
                  isAIGamer
                    ? 'Use the hidden legacy editor only for AIGamer answer templates or match-tuning fields that are not yet migrated into this page.'
                    : 'This assistant no longer needs a separate legacy templates page. Escalation above is the active runtime owner for the remaining settings on this path.'
                }}
              </div>
              <div v-if="isAIGamer" class="mt-3">
                <button type="button" class="btn o__secondary" @click="emit('openLegacy')">
                  Open legacy templates editor
                </button>
              </div>
            </div>
          </div>

          <div>
            <h3 class="text-h3 font-semibold mb-3">Recent escalations</h3>
            <div class="fin-table">
              <table>
                <thead>
                  <tr>
                    <th style="width: 40%">Trigger</th>
                    <th style="width: 20%">Outcome</th>
                    <th style="width: 20%">Channel</th>
                    <th style="width: 20%">Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isLoadingRecentEscalations">
                    <td class="fin-muted">Loading recent override hits...</td>
                    <td class="fin-muted">—</td>
                    <td class="fin-muted">—</td>
                    <td class="fin-muted">—</td>
                  </tr>
                  <tr v-else-if="!recentEscalationRows.length">
                    <td class="fin-muted">No recent attribute-driven escalations yet</td>
                    <td class="fin-muted">—</td>
                    <td class="fin-muted">—</td>
                    <td class="fin-muted">—</td>
                  </tr>
                  <tr v-for="row in recentEscalationRows" :key="row.id">
                    <td>
                      <div class="font-medium">{{ row.trigger }}</div>
                      <div v-if="row.detail" class="fin-muted text-xs mt-1">
                        {{ row.detail }}
                      </div>
                    </td>
                    <td class="uppercase text-xs font-semibold">{{ row.outcome }}</td>
                    <td>{{ row.channel }}</td>
                    <td>{{ row.time }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.escalation-owner-badge {
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 9999px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
}

.escalation-intro-card {
  border: 1px solid var(--fin-neutral-border);
  background: #fafaf9;
  border-radius: 14px;
  padding: 14px;
}

.escalation-inline-code {
  background: #f3f4f6;
  border-radius: 6px;
  padding: 1px 6px;
}

.escalation-loading-state {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #6b7280;
  font-size: 13px;
  padding: 12px 2px 20px;
}

.escalation-select,
.escalation-textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  line-height: 18px;
  padding: 10px 12px;
}

.escalation-select--inline {
  width: 180px;
}

.escalation-select--offset {
  margin-top: 16px !important;
}

.escalation-override-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.escalation-override-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  background: #ffffff;
}

.escalation-override-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.escalation-textarea {
  resize: vertical;
}

.escalation-select:focus,
.escalation-textarea:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.12);
}

:global(body.dark) .fin-ai,
:global(body.theme-mix) .fin-ai {
  color: #111827 !important;
}

:global(body.dark) .fin-ai .bg-base-module,
:global(body.theme-mix) .fin-ai .bg-base-module {
  background: #ffffff !important;
}

:global(body.dark) .fin-ai :is(h1, h2, h3, h4, th, .heading, .font-semibold, .fin-link),
:global(body.theme-mix) .fin-ai :is(h1, h2, h3, h4, th, .heading, .font-semibold, .fin-link) {
  color: #111827 !important;
}

:global(body.dark) .fin-ai .fin-muted,
:global(body.theme-mix) .fin-ai .fin-muted {
  color: #6b7280 !important;
}
</style>

